import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import Cookies from 'js-cookie';
import { BsClock } from 'react-icons/bs';
import { MdArrowBackIosNew } from 'react-icons/md';
import { Link, useNavigate } from 'react-router-dom';
import { IoIosSearch } from 'react-icons/io';
import { IoSettingsOutline } from 'react-icons/io5';
import ApiScaleGridEnfermagem from './ApiScaleGridEnfermagem';
import {
	requestBuscarGradeEscala,
	requestBuscarProfissionalEscala,
	requestCatalogosEscalaSelects,
	requestConsultarTrocaEscala,
	requestCriarItemEscala,
	requestEditarItemEscala,
	requestExcluirItemEscala,
	requestFormularioItemEscala,
	requestFormularioNovoItemEscala,
	requestListarSalasEscala,
	requestRegistrarTrocaEscala,
	requestVinculosEscala,
} from '../../services/escalaRequests';
import { canManageEscalaEnfermagem } from '../../utils/escalaFeatures';
import SearchableSetorSelect from '../Escalas Utils/SearchableSetorSelect';

const FILTERS_CACHE_KEY = 'escala.enfermagem.filters.cache';
const ESCALA_API_SCOPE = 'enfermagem';
const CPF_NOT_FOUND_WARNING =
	'profissional não cadastrado na base de dados, prossiga com o preenchimento do formulário para cadastrá-lo';

const semana = [
	'Segunda-feira',
	'Terça-feira',
	'Quarta-feira',
	'Quinta-feira',
	'Sexta-feira',
	'Sábado',
	'Domingo',
];

const UF_OPTIONS = [
	'AC',
	'AL',
	'AP',
	'AM',
	'BA',
	'CE',
	'DF',
	'ES',
	'GO',
	'MA',
	'MT',
	'MS',
	'MG',
	'PA',
	'PB',
	'PR',
	'PE',
	'PI',
	'RJ',
	'RN',
	'RS',
	'RO',
	'RR',
	'SC',
	'SP',
	'SE',
	'TO',
];

const DEFAULT_REGIME_OPTIONS = [
	{ value: 'rotina', label: 'Rotina' },
	{ value: 'plantonista', label: 'Plantonista' },
	{ value: 'diarista', label: 'Diarista' },
];

const MODAL_FIELD_HEIGHT = 36;

const SELECT_OPTION_PROPS = {
	className: 'text-[#4F5257] bg-white',
	style: { color: '#4F5257', backgroundColor: '#FFFFFF' },
};

const getDefaultSearchFilters = () => ({
	unidade: '',
	setor: '',
	setorId: '',
	regime: 'plantonista',
	servico: '',
	mesVigente: String(new Date().getMonth() + 1).padStart(2, '0'),
	anoVigente: String(new Date().getFullYear()),
});

const getCachedFilters = () => {
	const defaults = getDefaultSearchFilters();
	try {
		const raw = localStorage.getItem(FILTERS_CACHE_KEY);
		if (!raw) return defaults;
		return {
			...defaults,
			...JSON.parse(raw),
		};
	} catch (_error) {
		return defaults;
	}
};

const canSelectUnidadeFromUser = (user = {}) =>
	user?.cargo === 'desenvolvedor' ||
	String(user?.unidade || '').toLowerCase() === 'subhue' ||
	user?.is_subhue === true;

const canManageProfessionalsFromUser = canManageEscalaEnfermagem;

const areSearchFiltersEqual = (left = {}, right = {}) => {
	const keys = [
		'unidade',
		'setor',
		'setorId',
		'regime',
		'servico',
		'mesVigente',
		'anoVigente',
	];

	return keys.every(
		(key) => String(left?.[key] ?? '') === String(right?.[key] ?? ''),
	);
};

const createEmptyProfessional = () => ({
	profissionalId: '',
	vinculoId: '',
	cpf: '',
	nome: '',
	cargo: '',
	matricula: '',
	coren: '',
	numeroConselho: '',
	ufConselho: 'RJ',
	admissao: '',
	telefone: '',
	especialidade: '',
	vinculo: '',
	vinculoLabel: '',
	categoria: '',
	servico: '',
	setor: '',
	sala: '',
	dataSelecionada: '',
	horaInicio: '',
	horaFim: '',
	trocaCom: '',
	trocaComNome: '',
	trocaData: '',
	trocaHora: '',
	trocaItemDestinoId: '',
	selectedSlots: [],
	diasSelecionados: [],
	observacao: '',
});

const getErrorMessage = (result, fallback) => {
	const issueMessages = [
		result?.issues,
		result?.error?.issues,
		result?.error?.data?.issues,
		result?.error?.detail?.issues,
	]
		.filter(Array.isArray)
		.flat()
		.map((issue = {}) => String(issue?.mensagem || '').trim())
		.filter(Boolean);
	if (issueMessages.length > 0) return Array.from(new Set(issueMessages)).join(' ');

	if (!result?.error) return '';
	const detail = result.error?.detail;
	if (typeof detail === 'string') return detail;
	if (Array.isArray(detail)) return detail.join(' ');
	if (detail && typeof detail === 'object') {
		if (typeof detail.mensagem === 'string') return detail.mensagem;
		return JSON.stringify(detail);
	}
	return fallback;
};

const formatPhoneNumber = (value) => {
	if (!value) return '';
	let next = String(value).replace(/\D/g, '').slice(0, 11);
	if (next.length > 0) next = `(${next}`;
	if (next.length > 3) next = `${next.slice(0, 3)}) ${next.slice(3)}`;
	if (next.length > 10) next = `${next.slice(0, 10)}-${next.slice(10)}`;
	return next;
};

const normalizeUnits = (units = []) =>
	(units || [])
		.map((unit) => ({
			cnes: String(unit?.cnes || unit?.id || unit?.codigo || ''),
			nome: String(
				unit?.nome ||
					unit?.nome_fantasia ||
					unit?.razao_social ||
					unit?.label ||
					'--',
			),
		}))
		.filter((unit) => unit.cnes);

const normalizeSelectOptions = (
	items = [],
	{ preferLabelAsValue = false } = {},
) =>
	(items || [])
		.map((item) => {
			const label = String(
				item?.nome_exibicao ||
					item?.nome ||
					item?.label ||
					item?.descricao ||
					item?.identificador ||
					item?.value ||
					item ||
					'',
			).trim();
			const rawValue = preferLabelAsValue
				? label
				: item?.id || item?.codigo || item?.value || label;

			return {
				value: String(rawValue || '').trim(),
				label,
			};
		})
		.filter((item) => item.value && item.label);

const normalizeProfissionalBusca = (item = {}) => ({
	...item,
	id: item?.id || item?.profissional_id || item?.profissional || '',
	vinculoId:
		item?.vinculo_id ||
		item?.vinculoId ||
		item?.vinculos?.[0]?.id ||
		item?.vinculo?.id ||
		'',
	vinculoTipoId:
		item?.vinculo_tipo || item?.vinculo_tipo_id || item?.vinculoTipo || '',
	categoriaId: item?.categoria || item?.categoria_id || '',
	especialidadeId:
		item?.especialidade_id ||
		item?.especialidade?.id ||
		item?.especialidade ||
		'',
	nome: item?.nome || item?.nome_profissional || '',
	cpf: String(item?.cpf || '').replace(/\D/g, ''),
	numeroRegistro:
		item?.numero_registro || item?.conselho || item?.coren || '',
	ufConselho: item?.uf_conselho || item?.ufConselho || 'RJ',
	telefone: item?.telefone || '',
	cargo: item?.cargo || '',
	dataAdmissao: item?.data_admissao || item?.admissao || '',
	origem: item?.origem || item?.origem_dados || 'CNES',
});

const getMonthlyLabel = (mes, ano) => {
	const date = new Date(Number(ano), Number(mes) - 1, 1);
	return date.toLocaleDateString('pt-BR', {
		month: 'long',
		year: 'numeric',
	});
};

const createMonthDays = (mes, ano) => {
	const month = Number(mes) - 1;
	const year = Number(ano);
	const firstDay = new Date(year, month, 1);
	const totalDays = new Date(year, month + 1, 0).getDate();
	const leadingEmptyDays = (firstDay.getDay() + 6) % 7;
	const days = [];

	for (let index = 0; index < leadingEmptyDays; index += 1) {
		days.push(null);
	}

	for (let day = 1; day <= totalDays; day += 1) {
		const date = new Date(year, month, day);
		days.push({
			day,
			date,
			iso: `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
			diaSemana: semana[(date.getDay() + 6) % 7],
		});
	}

	return days;
};

const sanitizeHourValue = (value) => {
	let next = String(value ?? '')
		.replace(/\D/g, '')
		.slice(0, 2);

	if (next && Number.parseInt(next, 10) > 23) {
		next = '23';
	}

	return next;
};

const getFieldToneClass = (value) =>
	String(value ?? '').trim() ? 'text-[#6B7280]' : 'text-[#CBD3DD]';

const getModalFieldClass = (value, extraClass = '') =>
	`border-[1px] w-full outline-none border-[#E3E3E3] rounded-lg text-[14px] font-medium px-2.5 py-1.5 text-start bg-white transition-colors placeholder:text-[#D8DFE8] ${getFieldToneClass(value)} ${extraClass}`.trim();

const getModalDateFieldClass = (value, extraClass = '') =>
	getModalFieldClass(
		value,
		`text-[#6B7280] [color-scheme:light] ${extraClass}`.trim(),
	);

const resolveOptionLabel = (options = [], value, fallback = '') => {
	const normalized = String(value ?? '').trim();
	if (!normalized) return String(fallback ?? '').trim();
	const match = (options || []).find(
		(option) =>
			String(option?.id || option?.value || '').trim() === normalized,
	);
	return (
		match?.nome_exibicao ||
		match?.nome_exibição ||
		match?.nome ||
		match?.descricao ||
		match?.label ||
		String(fallback ?? normalized).trim()
	);
};

const formatDateLabel = (value) => {
	const raw = String(value ?? '').trim();
	if (!raw) return '';
	const parts = raw.split('-');
	if (parts.length !== 3) return raw;
	return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const normalizeSetorSearchValue = (value = '') =>
	String(value || '')
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.trim();

const normalizePopupStatus = (value = '') =>
	String(value || '')
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.trim();

const toUpperRegime = (value = '') =>
	String(value || '')
		.trim()
		.toUpperCase();

const buildMesReferencia = ({ mesVigente, anoVigente }) =>
	`${String(anoVigente)}-${String(mesVigente).padStart(2, '0')}-01`;

const formatHourPayload = (value = '') => {
	const base = String(value || '')
		.replace(/\D/g, '')
		.slice(0, 2);
	if (!base) return '';
	return `${base.padStart(2, '0')}:00:00`;
};

const digitsOnly = (value = '') => String(value || '').replace(/\D/g, '');

const getValidSelectedSlots = (slots = []) =>
	(slots || [])
		.filter((slot) => slot?.data)
		.map((slot) => ({
			...slot,
			data: String(slot.data),
			horaInicio: String(slot.horaInicio || '').padStart(2, '0'),
			horaFim: String(slot.horaFim || '').padStart(2, '0'),
		}))
		.sort((a, b) => a.data.localeCompare(b.data));

function FormScale() {
	const navigate = useNavigate();
	const [token, setToken] = useState('');
	const [userCnes, setUserCnes] = useState('');
	const [canSelectUnidade, setCanSelectUnidade] = useState(false);
	const [canManageProfessionals, setCanManageProfessionals] = useState(false);
	const didHydrateFiltersRef = useRef(false);
	const [isBootstrapping, setIsBootstrapping] = useState(true);
	const [carregando, setCarregando] = useState(false);
	const [isSubmittingPopup, setIsSubmittingPopup] = useState(false);
	const [searchFilters, setSearchFilters] = useState(getCachedFilters);
	const [appliedFilters, setAppliedFilters] = useState(getCachedFilters);
	const [gradePayload, setGradePayload] = useState(null);
	const [gradeMessage, setGradeMessage] = useState(
		'Selecione os filtros e clique em Buscar Escala.',
	);
	const [operationError, setOperationError] = useState('');
	const [unidadeOptions, setUnidadeOptions] = useState([]);
	const [vinculoOptions, setVinculoOptions] = useState([]);
	const [regimeOptions, setRegimeOptions] = useState(DEFAULT_REGIME_OPTIONS);
	const [categoriaOptions, setCategoriaOptions] = useState([]);
	const [, setEspecialidadeOptions] = useState([]);
	const [especialidadeEnfermagemOptions, setEspecialidadeEnfermagemOptions] =
		useState([]);
	const [cargoOptions, setCargoOptions] = useState([]);
	const [catalogoSetores, setCatalogoSetores] = useState([]);
	const [setorIdentificadores, setSetorIdentificadores] = useState([]);
	const [setorOptions, setSetorOptions] = useState([]);
	const [salaOptions, setSalaOptions] = useState([]);
	const [popupFormularioAssistivo, setPopupFormularioAssistivo] =
		useState(null);
	const [showAddScaleModal, setShowAddScaleModal] = useState(false);
	const [isModalLoading, setIsModalLoading] = useState(false);
	const [forceViewOnlyModal, setForceViewOnlyModal] = useState(false);
	const [popupStatus, setPopupStatus] = useState('');
	const [currentPro, setCurrentPro] = useState(createEmptyProfessional);
	const [unidadeSearchTerm, setUnidadeSearchTerm] = useState('');
	const [showUnidadeSuggestions, setShowUnidadeSuggestions] = useState(false);
	const [popupSubmitError, setPopupSubmitError] = useState('');
	const [cpfSearchWarning, setCpfSearchWarning] = useState('');
	const [isEditing, setIsEditing] = useState(false);
	const [editingItemId, setEditingItemId] = useState(null);
	const [editingItemSnapshot, setEditingItemSnapshot] = useState(null);
	const [currentSearch, setCurrentSearch] = useState([]);
	const [trocaConsulta, setTrocaConsulta] = useState(null);
	const [popupExtraFields, setPopupExtraFields] = useState({
		dataInicio: '',
		dataFim: '',
		observacao: '',
	});

	const targetEstabelecimento = useMemo(
		() => String(searchFilters.unidade || userCnes || '').trim(),
		[searchFilters.unidade, userCnes],
	);

	const monthDays = useMemo(
		() =>
			createMonthDays(searchFilters.mesVigente, searchFilters.anoVigente),
		[searchFilters.mesVigente, searchFilters.anoVigente],
	);

	const filteredSetorOptions = useMemo(
		() => catalogoSetores,
		[catalogoSetores],
	);

	const setorFilterOptions = useMemo(
		() => setorIdentificadores,
		[setorIdentificadores],
	);

	const selectedSearchSetor = useMemo(() => {
		const filtro = String(searchFilters.setor || '');
		return (
			setorIdentificadores.find((item) => {
				const identificador = String(
					item?.identificador || item?.value || '',
				);
				const setorId = String(
					item?.setor_id || item?.setorId || item?.id || '',
				);
				return identificador === filtro || setorId === filtro;
			}) || null
		);
	}, [searchFilters.setor, setorIdentificadores]);

	const profissionaisDaEscala = useMemo(() => {
		const origem = trocaConsulta?.candidatos || gradePayload?.itens || [];
		const mapa = new Map();
		origem.forEach((item) => {
			const nome = String(
				item?.nome_profissional || item?.resumo?.titulo || '',
			).trim();
			if (!nome || nome === 'VACÂNCIA') return;
			if (!mapa.has(nome)) {
				mapa.set(nome, {
					id: nome,
					nome,
				});
			}
		});
		return Array.from(mapa.values());
	}, [trocaConsulta?.candidatos, gradePayload?.itens]);

	const datasTrocaDisponiveis = useMemo(() => {
		if (!currentPro.trocaCom) return [];
		const mapa = new Map();
		(trocaConsulta?.candidatos || []).forEach((item) => {
			if (
				String(item?.nome_profissional || '') !==
				String(currentPro.trocaCom)
			) {
				return;
			}
			const data = String(
				item?.data || item?.dados_edicao?.data || '',
			).trim();
			if (!data) return;
			const [ano, mes, dia] = data.split('-');
			mapa.set(data, {
				value: data,
				label: `${String(dia).padStart(2, '0')}/${String(mes).padStart(2, '0')}/${ano}`,
			});
		});
		return Array.from(mapa.values());
	}, [trocaConsulta?.candidatos, currentPro.trocaCom]);

	const horasTrocaDisponiveis = useMemo(() => {
		if (!currentPro.trocaCom || !currentPro.trocaData) return [];
		return (trocaConsulta?.candidatos || [])
			.filter(
				(item) =>
					String(item?.nome_profissional || '') ===
						String(currentPro.trocaCom) &&
					String(item?.data || item?.dados_edicao?.data || '') ===
						String(currentPro.trocaData),
			)
			.map((item) => ({
				value: String(item?.id || item?.dados_edicao?.id || ''),
				itemId: Number(item?.id || item?.dados_edicao?.id || 0) || null,
				label: `${String(item?.hora_inicio || item?.dados_edicao?.hora_inicio || '').slice(0, 2)} às ${String(item?.hora_fim || item?.dados_edicao?.hora_fim || '').slice(0, 2)}`,
			}))
			.filter((item) => item.value);
	}, [trocaConsulta?.candidatos, currentPro.trocaCom, currentPro.trocaData]);

	const cargaHorariaMensal = useMemo(
		() =>
			(currentPro.selectedSlots || []).reduce((total, slot) => {
				const inicio = Number.parseInt(slot?.horaInicio || '', 10);
				const fim = Number.parseInt(slot?.horaFim || '', 10);
				if (Number.isNaN(inicio) || Number.isNaN(fim)) return total;
				const horas = fim >= inicio ? fim - inicio : 24 - inicio + fim;
				return total + horas;
			}, 0),
		[currentPro.selectedSlots],
	);

	const shouldShowCargaHorariaTotal = cargaHorariaMensal > 0;

	const normalizedCargoOptions = useMemo(
		() =>
			normalizeSelectOptions(cargoOptions, { preferLabelAsValue: true }),
		[cargoOptions],
	);

	const normalizedEspecialidadeEnfermagemOptions = useMemo(
		() => normalizeSelectOptions(especialidadeEnfermagemOptions),
		[especialidadeEnfermagemOptions],
	);

	const applyGradeResponse = useCallback(
		(response, filtersSource = {}) => {
			const estabelecimento = String(
				filtersSource?.unidade || userCnes || '',
			).trim();
			const nextAppliedFilters = {
				...getDefaultSearchFilters(),
				...filtersSource,
				unidade: estabelecimento,
			};

			setAppliedFilters((prev) =>
				areSearchFiltersEqual(prev, nextAppliedFilters)
					? prev
					: nextAppliedFilters,
			);
			setGradePayload(response || null);
			setGradeMessage(
				response?.visualizacao?.setores?.length
					? ''
					: 'Nenhuma escala encontrada para os filtros informados.',
			);
			setOperationError('');
			return true;
		},
		[userCnes],
	);

	const lockedProfessionalFields = useMemo(() => {
		const profissional = currentSearch[0] || null;
		return {
			nome: Boolean(profissional?.nome),
			cargo: Boolean(profissional?.cargo),
			numeroConselho: Boolean(profissional?.numeroRegistro),
			ufConselho: Boolean(profissional?.ufConselho),
			telefone: Boolean(profissional?.telefone),
			admissao: Boolean(profissional?.dataAdmissao),
			especialidade: Boolean(profissional?.especialidadeId),
			vinculo: Boolean(profissional?.vinculoTipoId),
			categoria: Boolean(profissional?.categoriaId),
		};
	}, [currentSearch]);

	const getLockedFieldStateClass = (isLocked) =>
		isLocked
			? '!bg-[#E5E7EB] text-[#7C8796] border-[#D1D5DB] cursor-not-allowed opacity-100'
			: '';

	const getLockedFieldStyle = (isLocked) =>
		isLocked
			? {
					backgroundColor: '#E5E7EB',
					color: '#7C8796',
					opacity: 1,
				}
			: {};

	const applyFormularioAssistivo = useCallback(
		(formulario, { presetDefaults = false } = {}) => {
			setPopupFormularioAssistivo(formulario || null);
			const catalogos = formulario?.catalogos || {};
			const nextSetores = catalogos?.setores_relacionados || [];
			if (
				Array.isArray(catalogos?.vinculos) &&
				catalogos.vinculos.length
			) {
				setVinculoOptions(catalogos.vinculos);
			}
			if (
				Array.isArray(catalogos?.especialidades) &&
				catalogos.especialidades.length
			) {
				setEspecialidadeOptions(catalogos.especialidades);
			}
			setEspecialidadeEnfermagemOptions(
				catalogos?.especialidades_enfermagem || [],
			);
			setCargoOptions(catalogos?.cargos || []);
			setCategoriaOptions(catalogos?.categorias || []);
			if (nextSetores.length) {
				setCatalogoSetores(nextSetores);
			}

			if (!presetDefaults) return;

			const defaults = formulario?.defaults || {};
			const item = formulario?.item || {};
			const contexto = formulario?.contexto_competencia || {};
			setCurrentPro((prev) => ({
				...prev,
				setor:
					String(
						prev.setor || contexto?.setor_id || item?.setor || '',
					) || '',
				categoria:
					String(
						prev.categoria ||
							item?.categoria_id ||
							defaults?.categoria_id ||
							'',
					) || '',
				especialidade:
					String(
						prev.especialidade ||
							item?.especialidade_id ||
							defaults?.profissional?.especialidade_id ||
							'',
					) || '',
				vinculo:
					String(
						prev.vinculo ||
							defaults?.profissional?.vinculo_tipo_id ||
							'',
					) || '',
			}));
		},
		[],
	);

	const applyCatalogosToFilters = useCallback(
		(catalogos, catalogosComItens) => {
			if (!catalogos || catalogos?.error) return;
			const hasCatalogosComItens =
				Boolean(catalogosComItens) && !catalogosComItens?.error;
			const setoresComItens =
				hasCatalogosComItens &&
				Array.isArray(catalogosComItens?.setores)
					? catalogosComItens.setores
					: null;
			const identificadoresComItens =
				hasCatalogosComItens &&
				Array.isArray(catalogosComItens?.identificadores)
					? catalogosComItens.identificadores
					: null;
			const identificadoresFiltrados =
				identificadoresComItens ??
				(setoresComItens
					? setoresComItens.map((setor = {}) => ({
							...setor,
							value: String(
								setor?.identificador ||
									setor?.value ||
									setor?.id ||
									'',
							),
							identificador:
								setor?.identificador ||
								setor?.value ||
								setor?.id ||
								'',
							setor_id:
								setor?.setor_id ||
								setor?.setorId ||
								setor?.id ||
								'',
							label:
								setor?.nome_exibicao ||
								setor?.nome_exibição ||
								setor?.nome ||
								setor?.identificador ||
								setor?.id ||
								'--',
							nome_exibicao:
								setor?.nome_exibicao ||
								setor?.nome_exibição ||
								setor?.nome ||
								setor?.identificador ||
								setor?.id ||
								'--',
						}))
					: null);

			const regimesCatalogo = catalogos?.regimes?.length
				? catalogos.regimes.map((item) => ({
						value: String(
							item?.value || item?.codigo || item?.id || '',
						),
						label: String(
							item?.label || item?.nome || item?.value || '',
						),
					}))
				: DEFAULT_REGIME_OPTIONS;

			// O catálogo desta função pode estar restrito ao estabelecimento
			// selecionado. A lista global do filtro é carregada separadamente para
			// que a troca de unidade nunca reduza as sugestões à escolha anterior.
			setCatalogoSetores(setoresComItens ?? (catalogos?.setores || []));
			setSetorIdentificadores(
				identificadoresFiltrados ?? (catalogos?.identificadores || []),
			);
			setEspecialidadeOptions(catalogos?.especialidades || []);
			setCategoriaOptions(catalogos?.categorias || []);
			setRegimeOptions(regimesCatalogo);

			setSearchFilters((prev) => {
				const cachedSetorId = String(prev.setorId || '').trim();
				const cachedSetor = String(prev.setor || '').trim();
				const setorIdentificadoresFiltro =
					identificadoresFiltrados ??
					(catalogos?.identificadores || []);
				const resolvedById = cachedSetorId
					? setorIdentificadoresFiltro.find((item) => {
							const setorId = String(
								item?.setor_id ||
									item?.setorId ||
									item?.id ||
									'',
							);
							return setorId === cachedSetorId;
						}) || null
					: null;
				const resolvedByValue = cachedSetor
					? setorIdentificadoresFiltro.find((item) => {
							const identificador = String(
								item?.identificador || item?.value || '',
							);
							const setorId = String(
								item?.setor_id ||
									item?.setorId ||
									item?.id ||
									'',
							);
							return (
								identificador === cachedSetor ||
								setorId === cachedSetor
							);
						}) || null
					: null;
				const resolvedSetor = resolvedById || resolvedByValue;
				if (!resolvedSetor && (cachedSetorId || cachedSetor)) {
					return { ...prev, setor: '', setorId: '' };
				}

				if (!resolvedSetor) return prev;

				return {
					...prev,
					setor:
						String(
							resolvedSetor?.identificador ||
								resolvedSetor?.value ||
								prev.setor ||
								'',
						) || '',
					setorId:
						String(
							resolvedSetor?.setor_id ||
								resolvedSetor?.setorId ||
								resolvedSetor?.id ||
								prev.setorId ||
								'',
						) || '',
				};
			});
		},
		[],
	);

	const refreshGrade = useCallback(
		async (filtersSource) => {
			if (!token) {
				setGradePayload(null);
				setGradeMessage(
					'Não foi possível autenticar a consulta da escala.',
				);
				return false;
			}

			const filtrosAtivos = filtersSource || getDefaultSearchFilters();
			const estabelecimento = String(
				filtrosAtivos.unidade || userCnes || '',
			).trim();
			const setorFiltro = String(filtrosAtivos.setor || '').trim();
			const setorIdFiltro = String(filtrosAtivos.setorId || '').trim();
			if (
				!estabelecimento ||
				(!setorFiltro && !setorIdFiltro) ||
				!filtrosAtivos.regime ||
				!filtrosAtivos.mesVigente ||
				!filtrosAtivos.anoVigente
			) {
				setGradePayload(null);
				setGradeMessage(
					'Selecione Unidade, Setor, Regime, Mês e Ano para buscar a grade.',
				);
				return false;
			}

			if (
				(setorFiltro || setorIdFiltro) &&
				setorIdentificadores.length === 0
			) {
				setGradePayload(null);
				setGradeMessage('Carregando filtros da unidade...');
				return false;
			}

			const setorSelecionado = setorIdentificadores.find((item) => {
				const identificador = String(
					item?.identificador || item?.value || '',
				);
				const setorId = String(
					item?.setor_id || item?.setorId || item?.id || '',
				);
				return (
					identificador === setorFiltro ||
					setorId === setorFiltro ||
					(setorIdFiltro && setorId === setorIdFiltro)
				);
			});
			const setorSelecionadoPorNome =
				!setorSelecionado && setorFiltro
					? setorIdentificadores.find((item) => {
							const nome = normalizeSetorSearchValue(
								item?.nome_exibicao || item?.nome || '',
							);
							return (
								nome &&
								nome === normalizeSetorSearchValue(setorFiltro)
							);
						})
					: null;
			const setorSelecionadoFinal =
				setorSelecionado || setorSelecionadoPorNome || null;
			const nextSetorFiltro = setorSelecionadoFinal
				? String(
						setorSelecionadoFinal?.identificador ||
							setorSelecionadoFinal?.value ||
							setorSelecionadoFinal?.setor_id ||
							setorSelecionadoFinal?.setorId ||
							setorSelecionadoFinal?.id ||
							setorFiltro ||
							'',
					) || setorFiltro
				: setorFiltro;
			const fallbackNumericSetorId =
				!setorSelecionadoFinal &&
				/^\d+$/.test(setorIdFiltro || setorFiltro)
					? setorIdFiltro || setorFiltro
					: '';
			const setorId =
				String(
					setorSelecionadoFinal?.setor_id ||
						setorSelecionadoFinal?.setorId ||
						setorSelecionadoFinal?.id ||
						fallbackNumericSetorId ||
						'',
				) || undefined;

			if ((setorFiltro || setorIdFiltro) && !setorId) {
				setGradePayload(null);
				setGradeMessage(
					'Selecione um setor válido para buscar a grade.',
				);
				return false;
			}

			const filtrosNormalizados =
				nextSetorFiltro && nextSetorFiltro !== setorFiltro
					? {
							...filtrosAtivos,
							setor: nextSetorFiltro,
							setorId: setorId || setorIdFiltro || '',
						}
					: filtrosAtivos;

			if (
				nextSetorFiltro !== setorFiltro ||
				(setorId && setorId !== setorIdFiltro)
			) {
				setSearchFilters((prev) => ({
					...prev,
					setor: nextSetorFiltro,
					setorId: setorId || setorIdFiltro || '',
				}));
			}

			setCarregando(true);
			setOperationError('');
			const response = await requestBuscarGradeEscala({
				token,
				scope: ESCALA_API_SCOPE,
				estabelecimento,
				setor: setorId,
				tipoSecao:
					setorSelecionadoFinal?.tipo_secao ||
					setorSelecionadoFinal?.tipoSecao ||
					undefined,
				regime: toUpperRegime(filtrosNormalizados.regime),
				mes: Number(filtrosNormalizados.mesVigente || 0) || undefined,
				ano: Number(filtrosNormalizados.anoVigente || 0) || undefined,
			});

			if (response?.error) {
				const message = getErrorMessage(
					response,
					'Não foi possível buscar a escala de enfermagem.',
				);
				setOperationError(message);
				setGradePayload(null);
				setGradeMessage(message);
				setCarregando(false);
				return false;
			}

			applyGradeResponse(response, {
				...filtrosNormalizados,
				unidade: estabelecimento,
			});
			setCarregando(false);
			return true;
		},
		[applyGradeResponse, setorIdentificadores, token, userCnes],
	);

	const loadTrocaOptions = useCallback(
		async (itemId) => {
			if (!token || !itemId) return;
			const response = await requestConsultarTrocaEscala({
				token,
				scope: ESCALA_API_SCOPE,
				id: itemId,
			});
			if (response?.error) {
				setTrocaConsulta(null);
				setPopupSubmitError(
					getErrorMessage(
						response,
						'Não foi possível carregar as opções de troca para este card.',
					),
				);
				return;
			}
			setTrocaConsulta(response);
		},
		[token],
	);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			const userCookie = Cookies.get('user');
			if (!userCookie) {
				setIsBootstrapping(false);
				setOperationError(
					'Usuário não autenticado para acessar a escala.',
				);
				return;
			}

			let parsedUser = null;
			try {
				parsedUser = JSON.parse(userCookie);
			} catch (_error) {
				setIsBootstrapping(false);
				setOperationError(
					'Não foi possível ler os dados do usuário autenticado.',
				);
				return;
			}

			const nextToken = parsedUser?.token || '';
			const nextCnes = String(
				parsedUser?.cnes ||
					parsedUser?.cnes_estabelecimento ||
					parsedUser?.cnesEstabelecimento ||
					'',
			).trim();
			const cachedUnidade = String(
				getCachedFilters().unidade || '',
			).trim();
			const nextCanSelectUnidade = canSelectUnidadeFromUser(parsedUser);
			const resolvedInitialUnidade = nextCanSelectUnidade
				? cachedUnidade || nextCnes
				: nextCnes;

			if (cancelled) return;
			setToken(nextToken);
			setUserCnes(nextCnes);
			setCanSelectUnidade(nextCanSelectUnidade);
			setCanManageProfessionals(
				canManageProfessionalsFromUser(parsedUser),
			);

			const [
				catalogos,
				catalogosComItens,
				catalogoUnidades,
				vinculos,
			] = await Promise.all([
				requestCatalogosEscalaSelects({
					token: nextToken,
					scope: ESCALA_API_SCOPE,
					estabelecimento: resolvedInitialUnidade || undefined,
				}),
				requestCatalogosEscalaSelects({
					token: nextToken,
					scope: ESCALA_API_SCOPE,
					estabelecimento: resolvedInitialUnidade || undefined,
					somenteComItens: true,
				}),
				requestCatalogosEscalaSelects({
					token: nextToken,
					scope: ESCALA_API_SCOPE,
				}),
				requestVinculosEscala({
					token: nextToken,
					scope: ESCALA_API_SCOPE,
				}),
			]);

			if (cancelled) return;

			applyCatalogosToFilters(catalogos, catalogosComItens);
			const unidadesGlobais = normalizeUnits(
				catalogoUnidades?.estabelecimentos || [],
			);
			const unidadesFallback = normalizeUnits(
				catalogos?.estabelecimentos || [],
			);
			setUnidadeOptions(
				unidadesGlobais.length ? unidadesGlobais : unidadesFallback,
			);
			if (!vinculos?.error && Array.isArray(vinculos)) {
				setVinculoOptions(vinculos);
			}

			if (resolvedInitialUnidade) {
				setSearchFilters((prev) => ({
					...prev,
					unidade: resolvedInitialUnidade,
				}));
				setAppliedFilters((prev) => ({
					...prev,
					unidade: resolvedInitialUnidade,
				}));
			}

			setIsBootstrapping(false);
		})();

		return () => {
			cancelled = true;
		};
	}, [applyCatalogosToFilters]);

	useEffect(() => {
		if (!token) {
			return;
		}
		if (!targetEstabelecimento) {
			return;
		}

		let cancelled = false;
		(async () => {
			const [response, responseComItens] = await Promise.all([
				requestCatalogosEscalaSelects({
					token,
					scope: ESCALA_API_SCOPE,
					estabelecimento: targetEstabelecimento || undefined,
				}),
				requestCatalogosEscalaSelects({
					token,
					scope: ESCALA_API_SCOPE,
					estabelecimento: targetEstabelecimento || undefined,
					somenteComItens: true,
				}),
			]);

			if (cancelled) return;
			if (response?.error) {
				setOperationError(
					getErrorMessage(
						response,
						'Não foi possível carregar os filtros da unidade selecionada.',
					),
				);
				return;
			}

			applyCatalogosToFilters(response, responseComItens);
		})();

		return () => {
			cancelled = true;
		};
	}, [token, targetEstabelecimento, applyCatalogosToFilters]);

	useEffect(() => {
		if (didHydrateFiltersRef.current) return;
		if (isBootstrapping || !token) return;
		if (!setorIdentificadores.length) return;
		const hasRequiredFilters =
			searchFilters.unidade &&
			(searchFilters.setor || searchFilters.setorId) &&
			searchFilters.regime &&
			searchFilters.mesVigente &&
			searchFilters.anoVigente;
		if (!hasRequiredFilters) return;
		didHydrateFiltersRef.current = true;
		setAppliedFilters((prev) =>
			areSearchFiltersEqual(prev, searchFilters) ? prev : searchFilters,
		);
	}, [isBootstrapping, searchFilters, setorIdentificadores.length, token]);

	useEffect(() => {
		setSetorOptions(filteredSetorOptions);
		setCurrentPro((prev) => {
			if (!prev.setor) return prev;
			const exists = filteredSetorOptions.some(
				(item) => String(item?.id || '') === String(prev.setor),
			);
			return exists ? prev : { ...prev, setor: '', sala: '' };
		});
	}, [filteredSetorOptions]);

	useEffect(() => {
		if (
			!token ||
			!showAddScaleModal ||
			!currentPro.setor ||
			!targetEstabelecimento
		) {
			setSalaOptions([]);
			return;
		}

		let cancelled = false;
		(async () => {
			const response = await requestListarSalasEscala({
				token,
				scope: ESCALA_API_SCOPE,
				setor: currentPro.setor,
				estabelecimento: targetEstabelecimento,
			});
			if (cancelled) return;
			if (response?.error) {
				setSalaOptions([]);
				return;
			}
			setSalaOptions(Array.isArray(response) ? response : []);
		})();

		return () => {
			cancelled = true;
		};
	}, [token, showAddScaleModal, currentPro.setor, targetEstabelecimento]);

	useEffect(() => {
		localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(appliedFilters));
	}, [appliedFilters]);

	useEffect(() => {
		// Enquanto a lista estiver aberta, o campo representa uma nova busca.
		// Não restaure o nome da unidade previamente selecionada sobre o que a
		// pessoa acabou de apagar ou digitar.
		if (showUnidadeSuggestions || !searchFilters.unidade) return;
		const selected = unidadeOptions.find(
			(item) => String(item.cnes) === String(searchFilters.unidade),
		);
		if (selected) {
			setUnidadeSearchTerm(selected.nome);
		}
	}, [searchFilters.unidade, unidadeOptions, showUnidadeSuggestions]);

	useEffect(() => {
		if (isBootstrapping || !token) return;
		if (
			!appliedFilters.unidade ||
			!appliedFilters.setor ||
			!appliedFilters.regime ||
			!appliedFilters.mesVigente ||
			!appliedFilters.anoVigente
		) {
			return;
		}
		refreshGrade(appliedFilters);
	}, [isBootstrapping, token, appliedFilters, refreshGrade]);

	const handleFilterInputChange = (fieldName, value) => {
		setSearchFilters((prev) => ({
			...prev,
			[fieldName]: value,
			...(fieldName === 'unidade' ? { setor: '' } : {}),
			...(fieldName === 'unidade' ? { setorId: '' } : {}),
		}));
		setOperationError('');
	};

	const handleSetorFilterChange = (value) => {
		const selected = setorIdentificadores.find((item) => {
			const identificador = String(
				item?.identificador || item?.value || '',
			);
			return identificador === String(value || '');
		});
		setSearchFilters((prev) => ({
			...prev,
			setor: value,
			setorId: String(
				selected?.setor_id || selected?.setorId || selected?.id || '',
			),
		}));
		setOperationError('');
	};

	const handleFilterSearch = async () => {
		await refreshGrade(searchFilters);
	};

	const openCreateModal = async () => {
		if (!token) {
			setOperationError(
				'Não foi possível autenticar o formulário da escala.',
			);
			return;
		}

		setIsEditing(false);
		setEditingItemId(null);
		setEditingItemSnapshot(null);
		setForceViewOnlyModal(false);
		setPopupStatus('');
		setPopupSubmitError('');
		setCpfSearchWarning('');
		setTrocaConsulta(null);
		setCurrentSearch([]);
		setPopupExtraFields({ dataInicio: '', dataFim: '', observacao: '' });
		setCurrentPro({
			...createEmptyProfessional(),
			setor: String(
				selectedSearchSetor?.setor_id ||
					selectedSearchSetor?.setorId ||
					selectedSearchSetor?.id ||
					searchFilters.setor ||
					'',
			),
		});
		setShowAddScaleModal(true);
		setIsModalLoading(true);

		try {
			const start = Date.now();
			const responseFormulario = await requestFormularioNovoItemEscala({
				token,
				scope: ESCALA_API_SCOPE,
				estabelecimento: targetEstabelecimento || undefined,
				setor:
					selectedSearchSetor?.setor_id ||
					selectedSearchSetor?.setorId ||
					selectedSearchSetor?.id ||
					undefined,
				tipoSecao:
					selectedSearchSetor?.tipo_secao ||
					selectedSearchSetor?.tipoSecao ||
					searchFilters.servico ||
					undefined,
				regime: toUpperRegime(searchFilters.regime),
				mes: Number(searchFilters.mesVigente || 0) || undefined,
				ano: Number(searchFilters.anoVigente || 0) || undefined,
			});
			const elapsed = Date.now() - start;
			if (elapsed < 1000) {
				await new Promise((resolve) =>
					setTimeout(resolve, 1000 - elapsed),
				);
			}

			if (responseFormulario?.error) {
				setPopupFormularioAssistivo(null);
				return;
			}

			applyFormularioAssistivo(responseFormulario, {
				presetDefaults: true,
			});
		} finally {
			setIsModalLoading(false);
		}
	};

	const closeModal = () => {
		setShowAddScaleModal(false);
		setIsModalLoading(false);
		setPopupSubmitError('');
		setCpfSearchWarning('');
		setPopupStatus('');
		setIsEditing(false);
		setEditingItemId(null);
		setEditingItemSnapshot(null);
		setForceViewOnlyModal(false);
		setCurrentSearch([]);
		setTrocaConsulta(null);
		setPopupFormularioAssistivo(null);
		setPopupExtraFields({ dataInicio: '', dataFim: '', observacao: '' });
		setCurrentPro(createEmptyProfessional());
	};

	const handlePopupStatusSelect = async (status) => {
		if (status === 'troca' && !isEditing) {
			setPopupSubmitError(
				'Troca de plantão só pode ser aberta a partir de um card existente.',
			);
			return;
		}
		const nextStatus = popupStatus === status ? '' : status;
		setPopupStatus(nextStatus);
		setPopupSubmitError('');

		if (nextStatus === 'troca' && editingItemId) {
			await loadTrocaOptions(editingItemId);
		} else {
			setTrocaConsulta(null);
		}
	};

	const handlePopupExtraFieldChange = (e) => {
		const { name, value } = e.target;
		setPopupExtraFields((prev) => ({
			...prev,
			[name]: value,
		}));
		if (name === 'observacao') {
			handleProInput('observacao', value);
		}
	};

	const handleProfessionalSearch = async () => {
		if (!token || !currentPro.cpf) return;

		setPopupSubmitError('');
		setCpfSearchWarning('');

		const response = await requestBuscarProfissionalEscala({
			token,
			scope: ESCALA_API_SCOPE,
			cpf: digitsOnly(currentPro.cpf),
			estabelecimento: targetEstabelecimento || undefined,
		});

		if (response?.error) {
			setCurrentSearch([]);
			const searchErrorMessage = getErrorMessage(
				response,
				'Não foi possível buscar profissional.',
			);
			const normalizedErrorMessage = String(searchErrorMessage || '')
				.toLowerCase()
				.normalize('NFD')
				.replace(/[\u0300-\u036f]/g, '');
			const isNotFoundMessage =
				normalizedErrorMessage.includes('nao encontrado') ||
				normalizedErrorMessage.includes('nao encontrada') ||
				normalizedErrorMessage.includes('nao cadastrado') ||
				normalizedErrorMessage.includes('not found');

			if (isNotFoundMessage) {
				setCpfSearchWarning(CPF_NOT_FOUND_WARNING);
				return;
			}

			setPopupSubmitError(searchErrorMessage);
			return;
		}

		if (!Array.isArray(response) || !response.length) {
			setCurrentSearch([]);
			setCpfSearchWarning(CPF_NOT_FOUND_WARNING);
			return;
		}

		const profissional = normalizeProfissionalBusca(response[0]);
		setCurrentSearch([profissional]);
		setPopupSubmitError('');
		setCpfSearchWarning('');
		setCurrentPro((prev) => ({
			...prev,
			profissionalId: String(profissional.id || ''),
			vinculoId: String(profissional.vinculoId || ''),
			nome: profissional.nome || prev.nome,
			cargo: profissional.cargo || prev.cargo,
			numeroConselho: profissional.numeroRegistro || prev.numeroConselho,
			coren: profissional.numeroRegistro || prev.coren,
			ufConselho: profissional.ufConselho || prev.ufConselho || 'RJ',
			telefone: profissional.telefone
				? formatPhoneNumber(profissional.telefone)
				: prev.telefone,
			admissao: profissional.dataAdmissao || prev.admissao,
			especialidade:
				String(
					profissional.especialidadeId || prev.especialidade || '',
				) || '',
			vinculo:
				String(profissional.vinculoTipoId || prev.vinculo || '') || '',
			categoria:
				String(profissional.categoriaId || prev.categoria || '') || '',
		}));
	};

	const handleProInput = (name, value) => {
		if (name === 'cpf') {
			const cpfDigitado = digitsOnly(value);
			const cpfBuscado = digitsOnly(currentSearch[0]?.cpf || '');
			if (cpfBuscado && cpfDigitado !== cpfBuscado) {
				setCurrentSearch([]);
			}
			setCpfSearchWarning('');
		}

		setCurrentPro((prev) => ({
			...prev,
			...(name === 'numeroConselho'
				? { numeroConselho: value, coren: value }
				: {}),
			[name]: value,
			...(name === 'setor' ? { sala: '' } : {}),
			...(name === 'trocaCom'
				? {
						trocaData: '',
						trocaHora: '',
						trocaItemDestinoId: '',
						trocaComNome:
							profissionaisDaEscala.find(
								(item) => String(item.id) === String(value),
							)?.nome || '',
					}
				: {}),
		}));
	};

	const handleCalendarDaySelect = (dateIso) => {
		setCurrentPro((prev) => {
			const selectedSlots = prev.selectedSlots || [];
			const jaExiste = selectedSlots.some(
				(item) => item.data === dateIso,
			);
			const nextSlots = jaExiste
				? selectedSlots.filter((item) => item.data !== dateIso)
				: [
						...selectedSlots,
						{ data: dateIso, horaInicio: '', horaFim: '' },
					].sort((a, b) => a.data.localeCompare(b.data));

			return {
				...prev,
				dataSelecionada: jaExiste
					? nextSlots[nextSlots.length - 1]?.data || ''
					: dateIso,
				selectedSlots: nextSlots,
				diasSelecionados: [
					...new Set(
						nextSlots
							.map(
								(slot) =>
									monthDays.find(
										(item) => item?.iso === slot.data,
									)?.diaSemana,
							)
							.filter(Boolean),
					),
				],
			};
		});
		setPopupSubmitError('');
	};

	const handleSelectedSlotChange = (slotDate, field, value) => {
		setCurrentPro((prev) => ({
			...prev,
			selectedSlots: (prev.selectedSlots || []).map((slot) =>
				slot.data === slotDate ? { ...slot, [field]: value } : slot,
			),
			...(prev.dataSelecionada === slotDate
				? { [field === 'horaInicio' ? 'horaInicio' : 'horaFim']: value }
				: {}),
		}));
		setPopupSubmitError('');
	};

	const handleSelectedSlotInputChange = (slotDate, field, value) => {
		handleSelectedSlotChange(slotDate, field, sanitizeHourValue(value));
	};

	const handleSelectedSlotBlur = (slotDate, field) => {
		const slot = (currentPro.selectedSlots || []).find(
			(item) => item.data === slotDate,
		);
		const currentValue = String(slot?.[field] || '');

		if (!currentValue) return;

		handleSelectedSlotChange(
			slotDate,
			field,
			currentValue.padStart(2, '0'),
		);
	};

	const buildProfessionalPayload = useCallback(() => {
		const profissionalSelecionado = currentSearch[0] || {};
		const profissionalId = Number(
			profissionalSelecionado?.id || currentPro.profissionalId || 0,
		);
		const vinculoId = Number(
			profissionalSelecionado?.vinculoId || currentPro.vinculoId || 0,
		);
		const vinculoTipo =
			Number(
				currentPro.vinculo ||
					profissionalSelecionado?.vinculoTipoId ||
					0,
			) || undefined;
		const categoria =
			Number(
				currentPro.categoria ||
					profissionalSelecionado?.categoriaId ||
					0,
			) || undefined;

		if (profissionalId && vinculoId) {
			return {
				id: profissionalId,
				vinculo_id: vinculoId,
			};
		}

		return {
			id: null,
			origem: profissionalSelecionado?.origem || 'CNES',
			cpf: digitsOnly(currentPro.cpf) || undefined,
			nome: currentPro.nome || undefined,
			conselho: 'COREN',
			numero_registro: currentPro.numeroConselho || undefined,
			uf_conselho: currentPro.ufConselho || undefined,
			cargo: currentPro.cargo || undefined,
			telefone: digitsOnly(currentPro.telefone) || undefined,
			data_admissao: currentPro.admissao || undefined,
			vinculo_tipo: vinculoTipo,
			categoria: categoria,
			cbo: profissionalSelecionado?.cbo || undefined,
		};
	}, [currentPro, currentSearch]);

	const buildBaseItemPayload = useCallback(
		(slot, { includeContext = true } = {}) => {
			const tipoSelecionado =
				normalizePopupStatus(popupStatus) || 'alocacao';
			const payload = {
				tipo_item: tipoSelecionado.toUpperCase(),
				observacao:
					popupExtraFields.observacao ||
					currentPro.observacao ||
					undefined,
				horario: {
					data: slot?.data,
					hora_inicio: formatHourPayload(slot?.horaInicio),
					hora_fim: formatHourPayload(slot?.horaFim),
				},
			};

			if (includeContext) {
				payload.contexto_competencia = {
					setor_id:
						Number(
							currentPro.setor ||
								selectedSearchSetor?.setor_id ||
								selectedSearchSetor?.setorId ||
								selectedSearchSetor?.id ||
								popupFormularioAssistivo?.contexto_competencia
									?.setor_id ||
								0,
						) || undefined,
					regime: toUpperRegime(searchFilters.regime),
					mes_referencia: buildMesReferencia(searchFilters),
				};
			}

			if (payload.tipo_item === 'VACANCIA') {
				payload.categoria_id =
					Number(
						currentPro.categoria ||
							popupFormularioAssistivo?.item?.categoria_id ||
							popupFormularioAssistivo?.defaults?.categoria_id ||
							0,
					) || undefined;
				payload.data_inicio_vacancia =
					popupExtraFields.dataInicio || slot?.data || undefined;
			} else {
				payload.profissional = buildProfessionalPayload();
			}

			return payload;
		},
		[
			buildProfessionalPayload,
			currentPro.categoria,
			currentPro.observacao,
			currentPro.setor,
			popupFormularioAssistivo,
			popupExtraFields.dataInicio,
			popupExtraFields.observacao,
			popupStatus,
			searchFilters,
			selectedSearchSetor,
		],
	);

	const handlePopupSubmit = async () => {
		if (!token) {
			setPopupSubmitError(
				'Não foi possível autenticar a operação da escala.',
			);
			return;
		}

		if (popupStatus === 'troca') {
			if (!editingItemId || !currentPro.trocaItemDestinoId) {
				setPopupSubmitError(
					'Selecione um destino válido para concluir a troca.',
				);
				return;
			}

			setIsSubmittingPopup(true);
			const responseTroca = await requestRegistrarTrocaEscala({
				token,
				scope: ESCALA_API_SCOPE,
				id: editingItemId,
				body: {
					item_destino_id: Number(currentPro.trocaItemDestinoId),
				},
			});
			setIsSubmittingPopup(false);

			if (responseTroca?.error) {
				setPopupSubmitError(
					getErrorMessage(
						responseTroca,
						'Não foi possível registrar a troca do plantão.',
					),
				);
				return;
			}

			closeModal();
			if (responseTroca?.visualizacao || responseTroca?.grade) {
				applyGradeResponse(responseTroca, searchFilters);
			} else {
				await refreshGrade(searchFilters);
			}
			return;
		}

		const slots = getValidSelectedSlots(currentPro.selectedSlots);
		if (!slots.length) {
			setPopupSubmitError('Adicione ao menos um dia e horário na lista.');
			return;
		}

		if (slots.some((slot) => !slot?.horaInicio || !slot?.horaFim)) {
			setPopupSubmitError(
				'Preencha hora inicial e final de todos os dias selecionados.',
			);
			return;
		}

		if (
			popupStatus !== 'vacancia' &&
			(!currentPro.nome || !digitsOnly(currentPro.cpf))
		) {
			setPopupSubmitError(
				'Nome e CPF são obrigatórios para itens com profissional.',
			);
			return;
		}

		if (popupStatus === 'vacancia' && !currentPro.categoria) {
			setPopupSubmitError(
				'Selecione a categoria para registrar a vacância.',
			);
			return;
		}

		setIsSubmittingPopup(true);
		setPopupSubmitError('');
		setOperationError('');

		if (isEditing && editingItemId) {
			const slotToPersist =
				slots.find(
					(slot) =>
						String(slot.data) ===
						String(
							editingItemSnapshot?.data ||
								editingItemSnapshot?.dados_edicao?.data ||
								'',
						),
				) || slots[0];

			const response = await requestEditarItemEscala({
				token,
				scope: ESCALA_API_SCOPE,
				id: editingItemId,
				body: buildBaseItemPayload(slotToPersist, {
					includeContext: false,
				}),
			});

			setIsSubmittingPopup(false);

			if (response?.error) {
				setPopupSubmitError(
					getErrorMessage(
						response,
						'Não foi possível atualizar o item da escala.',
					),
				);
				return;
			}

			closeModal();
			if (response?.visualizacao || response?.grade) {
				applyGradeResponse(response, searchFilters);
			} else {
				await refreshGrade(searchFilters);
			}
			return;
		}

		let lastResponse = null;
		for (const slot of slots) {
			const response = await requestCriarItemEscala({
				token,
				scope: ESCALA_API_SCOPE,
				body: buildBaseItemPayload(slot),
			});

			if (response?.error) {
				setIsSubmittingPopup(false);
				setPopupSubmitError(
					getErrorMessage(
						response,
						'Não foi possível criar um dos itens da escala.',
					),
				);
				return;
			}

			lastResponse = response;
		}

		setIsSubmittingPopup(false);
		closeModal();
		if (lastResponse?.visualizacao || lastResponse?.grade) {
			applyGradeResponse(lastResponse, searchFilters);
		} else {
			await refreshGrade(searchFilters);
		}
	};

	const handleDeleteItem = async (itemId) => {
		if (!token || !itemId) return;
		const response = await requestExcluirItemEscala({
			token,
			scope: ESCALA_API_SCOPE,
			id: itemId,
		});
		if (response?.error) {
			setOperationError(
				getErrorMessage(
					response,
					'Não foi possível excluir o item da escala.',
				),
			);
			return;
		}
		if (response?.visualizacao || response?.grade) {
			applyGradeResponse(response, searchFilters);
			return;
		}
		await refreshGrade(searchFilters);
	};

	const handleEditItem = async (item, forcedStatus = '') => {
		if (!token) return;

		const itemId = item?.id || item?.dados_edicao?.id;
		if (!itemId) {
			setOperationError(
				'Não foi possível identificar o item para edição.',
			);
			return;
		}

		setIsEditing(true);
		setEditingItemId(itemId);
		setEditingItemSnapshot(item);
		setForceViewOnlyModal(Boolean(item?.forceViewOnly));
		setCurrentSearch([]);
		setTrocaConsulta(null);
		setPopupSubmitError('');
		setShowAddScaleModal(true);
		setIsModalLoading(true);

		let responseFormulario;
		try {
			const start = Date.now();
			responseFormulario = await requestFormularioItemEscala({
				token,
				scope: ESCALA_API_SCOPE,
				id: itemId,
			});
			const elapsed = Date.now() - start;
			if (elapsed < 1000) {
				await new Promise((resolve) =>
					setTimeout(resolve, 1000 - elapsed),
				);
			}

			if (!responseFormulario?.error) {
				applyFormularioAssistivo(responseFormulario, {
					presetDefaults: true,
				});
			}
		} finally {
			setIsModalLoading(false);
		}

		const dados =
			responseFormulario?.item || item?.dados_edicao || item || {};
		const formularioProfissionalCpf =
			dados?.edicao?.profissional_por_cpf ||
			responseFormulario?.defaults?.profissional_por_cpf ||
			dados?.profissional_por_cpf ||
			{};
		const vinculoIdFormulario =
			dados?.vinculo?.id ||
			dados?.edicao?.profissional_por_cpf?.vinculo_tipo_id ||
			responseFormulario?.defaults?.profissional_por_cpf
				?.vinculo_tipo_id ||
			'';
		const vinculoNomeFormulario = String(dados?.vinculo?.nome || '').trim();
		const categoriaIdFormulario =
			dados?.categoria?.id ||
			dados?.edicao?.categoria_id ||
			responseFormulario?.defaults?.categoria_id ||
			'';
		const setorIdFormulario =
			dados?.edicao?.contexto_competencia?.setor_id ||
			responseFormulario?.contexto_competencia?.setor_id ||
			dados?.setor ||
			'';
		const dataItem = String(
			dados?.data || item?.data || item?.dados_edicao?.data || '',
		);
		const horaInicio = String(
			dados?.hora_inicio ||
				item?.hora_inicio ||
				item?.dados_edicao?.hora_inicio ||
				'',
		).slice(0, 2);
		const horaFim = String(
			dados?.hora_fim ||
				item?.hora_fim ||
				item?.dados_edicao?.hora_fim ||
				'',
		).slice(0, 2);

		setPopupStatus(
			forcedStatus ||
				normalizePopupStatus(dados?.tipo_item || item?.tipo_item),
		);
		setPopupExtraFields({
			dataInicio: String(dados?.data_inicio || dataItem || ''),
			dataFim: String(dados?.data_fim || dataItem || ''),
			observacao: String(
				dados?.observacao || item?.resumo?.observacao || '',
			),
		});
		setCurrentPro({
			...createEmptyProfessional(),
			profissionalId: String(
				dados?.profissional?.id || dados?.profissional_id || '',
			),
			vinculoId: String(
				dados?.profissional?.vinculo_id || dados?.vinculo_id || '',
			),
			cpf:
				formularioProfissionalCpf?.cpf || dados?.cpf || item?.cpf || '',
			nome:
				formularioProfissionalCpf?.nome ||
				dados?.nome ||
				item?.nome_profissional ||
				'',
			cargo: formularioProfissionalCpf?.cargo || dados?.cargo || '',
			matricula: dados?.matricula || '',
			coren: dados?.coren || dados?.numeroConselho || '',
			numeroConselho:
				formularioProfissionalCpf?.numero_registro ||
				dados?.numeroConselho ||
				dados?.coren ||
				'',
			ufConselho:
				formularioProfissionalCpf?.uf_conselho ||
				dados?.ufConselho ||
				dados?.uf_conselho ||
				'RJ',
			admissao:
				formularioProfissionalCpf?.data_admissao ||
				dados?.admissao ||
				dados?.data_admissao ||
				'',
			telefone: formularioProfissionalCpf?.telefone
				? formatPhoneNumber(formularioProfissionalCpf.telefone)
				: dados?.telefone
					? formatPhoneNumber(dados.telefone)
					: '',
			especialidade: String(
				dados?.especialidade || dados?.especialidade_id || '',
			),
			vinculo: String(
				vinculoIdFormulario ||
					dados?.vinculo_tipo ||
					dados?.vinculo_tipo_id ||
					'',
			),
			vinculoLabel: vinculoNomeFormulario,
			categoria: String(
				categoriaIdFormulario ||
					dados?.categoria_id ||
					dados?.categoria ||
					'',
			),
			servico: String(
				dados?.tipo_secao ||
					searchFilters.servico ||
					item?.tipo_secao ||
					'',
			),
			setor: String(setorIdFormulario || item?.setor || ''),
			sala: String(dados?.sala || item?.sala || ''),
			dataSelecionada: dataItem,
			horaInicio,
			horaFim,
			selectedSlots: dataItem
				? [
						{
							data: dataItem,
							horaInicio,
							horaFim,
						},
					]
				: [],
			diasSelecionados: dataItem
				? [
						monthDays.find((day) => day?.iso === dataItem)
							?.diaSemana,
					].filter(Boolean)
				: [],
			observacao: String(
				dados?.observacao || item?.resumo?.observacao || '',
			),
		});

		if (forcedStatus === 'troca') {
			await loadTrocaOptions(itemId);
		}
	};

	const handleSwapItem = async (item) => {
		await handleEditItem(item, 'troca');
	};

	const filteredUnidades = unidadeOptions.filter((unit) =>
		unit.nome.toLowerCase().includes(unidadeSearchTerm.toLowerCase()),
	);

	const shouldShowLoader = isBootstrapping || carregando;
	const hasSelectedSlots = (currentPro.selectedSlots || []).length > 0;
	const isViewOnlyModal = isEditing && forceViewOnlyModal;

	return (
		<div className='flex flex-col mx-auto min-h-screen'>
			<div className='text-center flex-grow mt-[64px] sm:mt-[80px] '>
				<div className='flex flex-col w-[100%] lg:w-[100%] mx-auto text-wrap text-center flex-grow bg-[#F0F4F8]'>
					<div className='mx-[7%] text-center md:text-center flex justify-center text-[#13335A] font-semibold text-[26px] sm:text-[28px] py-[28px] sm:py-[36px]'>
						Escala de Enfermagem
					</div>
				</div>

				<div className='flex my-[18px] flex-col items-center w-full mx-auto rounded-md mb-8 px-2 sm:px-0'>
					<div className='w-[90%] sm:w-[95%] flex justify-start gap-2  py-5 items-center max-w-[1600px]'>
						<Link
							to='/escala'
							className='inline-flex items-center  gap-1.5 text-[18px] font-medium text-[#17395f]  hover:text-[#60758f] duration-300 transition-colors mb-4'>
							<MdArrowBackIosNew size={15} />
							Voltar
						</Link>
					</div>
					<div className='w-[90%] sm:w-[95%] flex flex-col gap-2 border border-gray-100 rounded-lg shadow-md p-5 items-center max-w-[1600px] bg-white'>
						<p className='text-[#13335A] font-medium text-[24px] text-start w-full'>
							Filtros de pesquisa
						</p>
						<hr className='w-full border-t border-gray-200 my-2' />

						{canSelectUnidade && (
							<div className='w-full self-start mb-3 relative'>
								<label className='font-medium text-[16px] text-start text-[#13335A] block'>
									Unidade
								</label>
								<input
									type='text'
									value={unidadeSearchTerm}
									onChange={(e) => {
										setUnidadeSearchTerm(e.target.value);
										setShowUnidadeSuggestions(true);
										const match = unidadeOptions.find(
											(item) =>
												item.nome.toLowerCase() ===
												e.target.value.toLowerCase(),
										);
										handleFilterInputChange(
											'unidade',
											match ? match.cnes : '',
										);
									}}
									onFocus={() => {
										setShowUnidadeSuggestions(true);
									}}
									onBlur={() =>
										setTimeout(
											() =>
												setShowUnidadeSuggestions(
													false,
												),
											120,
										)
									}
									placeholder='Selecione uma unidade'
									className='border rounded-[8px] px-3 py-2 mt-1 w-full font-medium text-[#6b7280] border-[#E3E3E3]'
								/>
								{showUnidadeSuggestions && (
									<div className='absolute left-0 right-0 top-full mt-1 z-30 w-full max-h-[220px] overflow-y-auto rounded-[8px] border border-[#E3E3E3] bg-white shadow-sm'>
										{filteredUnidades.map((unit) => (
											<button
												type='button'
												key={unit.cnes}
												onMouseDown={(e) => {
													e.preventDefault();
													setUnidadeSearchTerm(
														unit.nome,
													);
													handleFilterInputChange(
														'unidade',
														unit.cnes,
													);
													setShowUnidadeSuggestions(
														false,
													);
												}}
												className='block w-full px-3 py-2 text-left text-[14px] text-[#4F5257] hover:bg-[#F3F4F6]'>
												{unit.nome}
											</button>
										))}
									</div>
								)}
							</div>
						)}

						<div className='flex flex-col md:flex-row justify-between items-end gap-4 md:gap-[32px] w-full'>
							<div className='flex flex-col w-full'>
								<label className='font-medium text-[18px] text-start text-[#13335A]'>
									Setor
								</label>
								<SearchableSetorSelect
									value={searchFilters.setor}
									options={setorFilterOptions}
									onChange={handleSetorFilterChange}
									className='mt-1 rounded-lg border border-gray-300 px-3 py-2 font-semibold text-[#949494] outline-none'
									getOptionValue={(setor) =>
										String(
											setor?.identificador || setor?.value || '',
										)
									}
									getOptionLabel={(setor) =>
										setor?.nome_exibicao || setor?.nome_exibição || '--'
									}
								/>
							</div>

							<div className='flex flex-col w-full'>
								<label className='font-medium text-[18px] text-start text-[#13335A]'>
									Regime
								</label>
								<select
									value={searchFilters.regime}
									onChange={(e) =>
										handleFilterInputChange(
											'regime',
											e.target.value,
										)
									}
									className='border border-gray-300 rounded-lg px-3 py-2 mt-1 w-full font-semibold text-[#949494] appearance-none'>
									{regimeOptions.map((option) => (
										<option
											key={option.value}
											value={String(
												option.value,
											).toLowerCase()}>
											{option.label}
										</option>
									))}
								</select>
							</div>

							<div className='bg-white w-full md:w-auto md:min-w-[320px] rounded-lg'>
								<p className='text-[16px] text-[#13335A] font-medium mb-2 text-start'>
									Data vigente
								</p>
								<div className='flex gap-2'>
									<div className='flex-1 min-w-[120px]'>
										<label className='font-medium text-[14px] text-start text-[#13335A] block mb-1'>
											Mês
										</label>
										<select
											value={searchFilters.mesVigente}
											onChange={(e) =>
												handleFilterInputChange(
													'mesVigente',
													e.target.value,
												)
											}
											className='border border-gray-300 rounded-lg px-3 py-2 w-full text-[#949494]'>
											{Array.from(
												{ length: 12 },
												(_, i) => {
													const value = String(
														i + 1,
													).padStart(2, '0');
													return (
														<option
															key={value}
															value={value}>
															{new Date(
																2026,
																i,
																1,
															).toLocaleDateString(
																'pt-BR',
																{
																	month: 'long',
																},
															)}
														</option>
													);
												},
											)}
										</select>
									</div>
									<div className='flex-1 min-w-[120px]'>
										<label className='font-medium text-[14px] text-start text-[#13335A] block mb-1'>
											Ano
										</label>
										<select
											value={searchFilters.anoVigente}
											onChange={(e) =>
												handleFilterInputChange(
													'anoVigente',
													e.target.value,
												)
											}
											className='border border-gray-300 rounded-lg px-3 py-2 w-full text-[#949494]'>
											{Array.from(
												{ length: 5 },
												(_, i) => {
													const year = String(
														new Date().getFullYear() -
															1 +
															i,
													);
													return (
														<option
															key={year}
															value={year}>
															{year}
														</option>
													);
												},
											)}
										</select>
									</div>
								</div>
							</div>
						</div>

						<div className='flex justify-center w-full'>
							<button
								onClick={handleFilterSearch}
								className='bg-[#FFFFFF] text-[#13335A] border border-[#ebebeb] text-[16px] mt-5 px-4 py-2 rounded-md hover:bg-[#13335A] hover:text-white hover:border-[#13335A] transition-all duration-300 shadow-md font-inter'
								style={{ width: '100%', maxWidth: '200px' }}>
								<IoIosSearch className='inline-block mr-2 w-[20px] h-auto' />
								Buscar Escala
							</button>
						</div>
					</div>
				</div>

				<div className='flex flex-col items-center'>
					<div className='flex flex-col max-w-[1600px] w-[90%] sm:w-[95%] mt-4 items-center'>
						<div className='flex flex-col sm:flex-row items-center justify-center gap-3 w-full'>
							{/* <button
								className='flex items-center justify-center gap-2 bg-[#0B4F7A] text-white px-4 py-2 hover:bg-[#083B5B] transition w-full sm:w-[210px] rounded-[7px]'
								onClick={openCreateModal}>
								<span className='text-2xl leading-none mb-1 flex items-center'>
									+
								</span>
								<span className='inline-block w-[2px] h-6 bg-[#004A80] rounded-full'></span>
								<span className='flex items-center h-full'>
									Adicionar Escala
								</span>
							</button> */}
							{canManageProfessionals && (
								<button
									onClick={() =>
										navigate('/escala-profissionais', {
											state: {
												escalaOrigem: 'enfermagem',
											},
										})
									}
									className='flex w-[190px] sm:w-auto items-center justify-center gap-2 sm:gap-3 bg-[#004A80] text-[#FFFFFF] px-3 py-1.5 text-[13px] sm:px-4 sm:py-2 sm:text-base font-light hover:bg-[#0d466c] transition rounded-[7px] shadow-md'>
									<IoSettingsOutline className='w-[16px] h-[16px] sm:w-[20px] sm:h-[20px]' />
									<span className='inline-block w-[2px] h-5 sm:h-6 bg-[#003D69] rounded-full'></span>
									<span className='flex items-center h-full font-inter'>
										Gerenciar Profissionais
									</span>
								</button>
							)}
						</div>
					</div>
				</div>

				{operationError && (
					<div className='mx-auto mt-4 w-[90%] sm:w-[95%] max-w-[1600px] rounded-lg border border-[#F5C2C7] bg-[#FDECEE] px-4 py-3 text-start text-[14px] text-[#A61B29]'>
						{operationError}
					</div>
				)}

				{shouldShowLoader ? (
					<div className='flex justify-center h-[300px] content-center w-full my-20'>
						<div className='w-8 h-8 rounded-full border-4 border-[#dbeafe] border-t-[#2563eb] animate-spin' />
					</div>
				) : gradePayload ? (
					<ApiScaleGridEnfermagem
						payload={gradePayload}
						onEditItem={handleEditItem}
						canManageItems={Boolean(token)}
					/>
				) : (
					<div className='w-full text-center py-14 text-[#757575] text-[18px] font-medium'>
						{gradeMessage}
					</div>
				)}
			</div>

			{showAddScaleModal && (
				<div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-2 sm:p-4 overflow-y-auto'>
					<div className='bg-white rounded-lg shadow-2xl max-w-[700px] w-[96%] sm:w-[92%] relative border border-[#E3E3E3] overflow-hidden max-h-[95vh] sm:max-h-[90vh]'>
						<div className='w-full bg-[#F1F1F1] h-8 flex items-center justify-end px-3'>
							{/* {isEditing && (
								<span className='mr-auto text-[12px] font-medium text-[#0d466c]'>
									Atualizando card #{editingItemId}
								</span>
							)} */}
							<button
								onClick={closeModal}
								className='text-[#BDBDBD] transition-all duration-300 hover:text-[#888888] text-2xl font-bold mb-1'>
								&times;
							</button>
						</div>
						<div className='py-4 px-3 sm:px-4 flex flex-col gap-3 relative overflow-y-auto max-h-[calc(95vh-2rem)] sm:max-h-[calc(90vh-2rem)]'>
							{isModalLoading ? (
								<div className='w-full min-h-[280px] flex flex-col items-center justify-center gap-4'>
									<div className='animate-spin h-12 w-12 rounded-full border-4 border-[#E5E7EB] border-t-[#3F7BA7]' />
									{/* <p className='text-[#4F5257] text-[16px] font-medium'>
										Carregando formulário...
									</p> */}
									<div className='space-y-2 w-full max-w-[520px]'>
										<div className='h-4 rounded-full bg-[#E5E7EB]' />
										<div className='h-4 rounded-full bg-[#E5E7EB] w-[90%]' />
										<div className='h-4 rounded-full bg-[#E5E7EB] w-[75%]' />
									</div>
								</div>
							) : isViewOnlyModal ? (
								(() => {
									const tipo = (
										popupStatus || 'alocacao'
									).toLowerCase();
									const colorByTipo = {
										vacancia: '#BD504E',
										ferias: '#6ACEF8',
										atestado: '#FB965C',
										folga: '#8BBD8B',
										licenca: '#A476C7',
										troca: '#7A4B3A',
										falta: '#545454',
										alocacao: '#4D6C92',
									};
									const labelByTipo = {
										vacancia: 'Vacância',
										ferias: 'Férias',
										atestado: 'Atestado',
										folga: 'Folga',
										licenca: 'Licença',
										troca: 'Troca',
										falta: 'Falta',
										alocacao: 'Alocação',
									};
									const cor = colorByTipo[tipo] || '#6ACEF8';
									const label = labelByTipo[tipo] || tipo;
									const fmtDate = (iso) => {
										if (!iso) return '';
										const parts = String(iso).split('-');
										return `${parts[2]}/${parts[1]}/${parts[0]}`;
									};
									let badgeText = label;
									if (tipo === 'vacancia') {
										badgeText = `Vacância a partir de ${fmtDate(popupExtraFields.dataInicio) || '--/--/----'}`;
									} else if (tipo === 'ferias') {
										badgeText = `Férias até ${fmtDate(popupExtraFields.dataFim) || '--/--/----'}`;
									} else if (
										tipo === 'atestado' ||
										tipo === 'folga' ||
										tipo === 'licenca'
									) {
										badgeText = `${label} até ${fmtDate(popupExtraFields.dataFim) || '--/--/----'}`;
									} else if (tipo === 'troca') {
										badgeText = 'Troca com:';
									} else if (tipo === 'falta') {
										badgeText = 'FALTA';
									} else {
										badgeText = `Alocação ${fmtDate(popupExtraFields.dataInicio) || '--/--/----'}`;
									}
									const nomePro =
										currentSearch.length > 0
											? currentSearch[0].nome
											: currentPro.nome;
									const numeroConselhoPro =
										currentPro.numeroConselho || '';
									const vinculoLabel =
										currentPro.vinculoLabel ||
										resolveOptionLabel(
											vinculoOptions,
											currentPro.vinculo,
											currentPro.vinculo,
										) ||
										'--';
									const admissaoLabel =
										fmtDate(currentPro.admissao) ||
										'--/--/----';
									const trocaComNome =
										currentPro.trocaComNome || '';
									const isVacanciaCard = tipo === 'vacancia';
									const isFaltaCard = tipo === 'falta';
									const badgeFieldValue =
										tipo === 'troca'
											? trocaComNome || ''
											: popupExtraFields.observacao;
									const categoriaLabel =
										resolveOptionLabel(
											categoriaOptions,
											currentPro.categoria,
											currentPro.categoria,
										) || '';
									const setorLabel =
										resolveOptionLabel(
											setorOptions,
											currentPro.setor,
											currentPro.setor,
										) || '';
									const fieldCls =
										'border border-[#E3E3E3] rounded-lg px-2 py-1.5 sm:px-3 sm:py-2 text-[12px] sm:text-[14px] leading-snug text-[#6B7280] bg-white min-h-[34px] sm:min-h-[40px] flex items-center break-words whitespace-normal';
									const labelCls =
										'text-[13px] sm:text-[16px] text-start text-[#13335A] mb-1';
									const regimeLabel =
										regimeOptions.find(
											(option) =>
												String(
													option.value || '',
												).toLowerCase() ===
												String(
													searchFilters.regime || '',
												).toLowerCase(),
										)?.label ||
										String(
											searchFilters.regime || '',
										).toLowerCase() === 'plantonista'
											? 'Plantonista'
											: String(
														searchFilters.regime ||
															'',
												  ).trim()
												? 'Rotina'
												: '';
									const vacanciaTurnoLabel = (
										currentPro.selectedSlots || []
									)
										.map((slot) => {
											const data = fmtDate(
												slot?.data || '',
											);
											const inicio = String(
												slot?.horaInicio || '',
											).padStart(2, '0');
											const fim = String(
												slot?.horaFim || '',
											).padStart(2, '0');
											if (!data) return '';
											const faixa = [inicio, fim]
												.filter(Boolean)
												.map((hora) => `${hora}:00`)
												.join(' às ');
											return faixa
												? `${data} ${faixa}`
												: data;
										})
										.filter(Boolean)
										.join(' • ');
									return (
										<div className='flex flex-col gap-3 w-full font-inter'>
											{!isVacanciaCard && (
												<p className='font-semibold text-[16px] sm:text-[20px] leading-tight break-words text-[#13335A] flex justify-start'>
													{nomePro || ''}
												</p>
											)}
											{!isVacanciaCard &&
												!badgeText.includes(
													'Alocação',
												) && (
													<div>
														<div className='flex items-center gap-3'>
															<div
																style={{
																	backgroundColor:
																		cor,
																}}
																className={`rounded-lg px-2 py-1.5 sm:px-3 sm:py-2 flex w-full flex-col sm:flex-row items-stretch sm:items-center content-center gap-2 sm:gap-3 text-white text-[12px] sm:text-[14px] whitespace-normal sm:whitespace-nowrap shrink-0 ${isFaltaCard ? 'justify-center' : ''}`}>
																<span
																	className={
																		isFaltaCard
																			? 'w-full text-center 	text-[16px]'
																			: ''
																	}>
																	{badgeText}
																</span>
																{!isFaltaCard && (
																	<div
																		className={`${fieldCls} flex-1 font-semibold`}
																		style={{
																			borderColor:
																				cor,
																		}}>
																		{
																			badgeFieldValue
																		}
																	</div>
																)}
															</div>
														</div>
													</div>
												)}
											{!isVacanciaCard && <hr></hr>}
											{!isVacanciaCard ? (
												<>
													<div className='grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4'>
														<div>
															<p
																className={
																	labelCls
																}>
																Admissão
															</p>
															<div
																className={
																	fieldCls
																}>
																{admissaoLabel}
															</div>
														</div>
														<div>
															<p
																className={
																	labelCls
																}>
																Vínculo
															</p>
															<div
																className={
																	fieldCls
																}>
																{vinculoLabel}
															</div>
														</div>
													</div>

													<div className='grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4'>
														<div>
															<p
																className={
																	labelCls
																}>
																Cargo
															</p>
															<div
																className={
																	fieldCls
																}>
																{currentPro.cargo ||
																	''}
															</div>
														</div>
														<div>
															<div className='flex gap-2'>
																<p
																	className={`${labelCls} flex-1`}>
																	Número do
																	conselho
																</p>
																<p
																	className={`${labelCls} w-[80px]`}>
																	UF
																</p>
															</div>
															<div className='flex gap-2'>
																<div
																	className={`${fieldCls} flex-1`}>
																	{
																		numeroConselhoPro
																	}
																</div>
																<div className='w-[80px]'>
																	<div
																		className={
																			fieldCls
																		}>
																		{currentPro.ufConselho ||
																			''}
																	</div>
																</div>
															</div>
														</div>
													</div>

													<div className='grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4'>
														<div>
															<p
																className={
																	labelCls
																}>
																Categoria
															</p>
															<div
																className={
																	fieldCls
																}>
																{categoriaLabel}
															</div>
														</div>
														<div>
															<p
																className={
																	labelCls
																}>
																Setor
															</p>
															<div
																className={
																	fieldCls
																}>
																{setorLabel}
															</div>
														</div>
													</div>
												</>
											) : (
												<div className='w-full rounded-lg bg-white p-3 sm:p-3'>
													<div
														style={{
															backgroundColor:
																cor,
														}}
														className='w-full rounded-lg px-4 py-3 text-center text-[20px] font-semibold uppercase tracking-wide text-white'>
														Vacância
													</div>
													<div className='mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4'>
														<div>
															<p
																className={
																	labelCls
																}>
																Início da
																vacância
															</p>
															<div
																className={
																	fieldCls
																}>
																{fmtDate(
																	popupExtraFields.dataInicio,
																) ||
																	'--/--/----'}
															</div>
														</div>
														<div>
															<p
																className={
																	labelCls
																}>
																Categoria
															</p>
															<div
																className={
																	fieldCls
																}>
																{categoriaLabel ||
																	'--'}
															</div>
														</div>
														<div>
															<p
																className={
																	labelCls
																}>
																Regime
															</p>
															<div
																className={
																	fieldCls
																}>
																{regimeLabel ||
																	'--'}
															</div>
														</div>
														<div>
															<p
																className={
																	labelCls
																}>
																Horários
															</p>
															<div
																className={`${fieldCls} items-start py-3`}>
																<span className='whitespace-pre-wrap break-words'>
																	{vacanciaTurnoLabel ||
																		'--'}
																</span>
															</div>
														</div>
													</div>
													<div className='mt-4'>
														<p className={labelCls}>
															Observação
														</p>
														<div
															className={`${fieldCls} min-h-[120px] items-start py-3`}>
															<span className='whitespace-pre-wrap break-words'>
																{popupExtraFields.observacao ||
																	'--'}
															</span>
														</div>
													</div>
												</div>
											)}
										</div>
									);
								})()
							) : (
								<>
									<div className='w-full flex justify-center items-center'>
										<div className='flex flex-col gap-1 w-full sm:w-auto'>
											<div className='flex flex-col sm:flex-row justify-center font-inter gap-2 sm:gap-3 items-stretch sm:items-center w-full sm:w-auto'>
												<p className='font-inter font-medium text-[#13335A]'>
													CPF
												</p>
												<input
													type='text'
													value={currentPro.cpf}
													onChange={(e) =>
														handleProInput(
															'cpf',
															e.target.value.slice(
																0,
																11,
															),
														)
													}
													placeholder='12345678900'
													className='border border-[#E3E3E3] rounded-lg py-1.5 outline-none px-3 text-[#666] w-full sm:w-[190px] bg-white'
												/>
												<button
													onClick={
														handleProfessionalSearch
													}
													className='px-[8px] py-[6px] sm:py-[3px] rounded-md border border-[#E3E3E3] transition-all shadow-md duration-200 w-full text-[14px] sm:w-auto text-[#4F5257] hover:bg-[#e9e9e9]'>
													Pesquisar
												</button>
											</div>
											{cpfSearchWarning && (
												<span className='text-[12px] leading-tight text-[#dc2626] sm:max-w-[300px]'>
													{cpfSearchWarning}
												</span>
											)}
										</div>
									</div>

									<div className='w-full flex justify-start sm:justify-center gap-2 sm:gap-3 flex-wrap '>
										{[
											{
												key: 'ferias',
												label: 'Férias',
												color: 'bg-[#42B9EB]',
												border: 'border border-[#2A95C2]',
											},
											{
												key: 'atestado',
												label: 'Atestado',
												color: 'bg-[#EB6D21]',
												border: 'border border-[#B14A16]',
											},
											{
												key: 'folga',
												label: 'Folga',
												color: 'bg-[#0B6B3A]',
												border: 'border border-[#06441F]',
											},
											{
												key: 'troca',
												label: 'Troca',
												color: 'bg-[#7A4B3A]',
												border: 'border border-[#5A372B]',
											},
											{
												key: 'licenca',
												label: 'Licença',
												color: 'bg-[#8D148F]',
												border: 'border border-[#620E63]',
											},
											{
												key: 'vacancia',
												label: 'Vacância',
												color: 'bg-[#B81A18]',
												border: 'border border-[#79120F]',
											},
										].map((status) => {
											const ativo =
												popupStatus === status.key;
											const isDisabled =
												status.key === 'troca' &&
												!isEditing;
											return (
												<button
													key={status.key}
													type='button'
													disabled={isDisabled}
													onClick={() =>
														handlePopupStatusSelect(
															status.key,
														)
													}
													className={`${status.color} ${status.border} text-white rounded-md h-[32px] w-full sm:w-[127px] gap-2 px-3 py-1 flex items-center justify-center text-[15px] font-normal disabled:opacity-50 disabled:cursor-not-allowed`}>
													<span className='leading-3 text-[15px]'>
														{status.label}
													</span>
													<span className='relative flex items-center justify-center'>
														<span
															className={`block ${ativo ? 'bg-[#13335A]' : 'bg-[#cfcfcf]'} h-[18px] w-[36px] rounded-full`}
														/>
														<span
															className={`absolute h-[14px] w-[14px] rounded-full bg-white/90 transition-all ${ativo ? 'translate-x-[9px]' : '-translate-x-[9px]'}`}
														/>
													</span>
												</button>
											);
										})}
									</div>

									{popupStatus === 'ferias' && (
										<div className='w-full grid grid-cols-1 sm:grid-cols-2 gap-3 bg-[#fcfcfc] p-2 rounded-lg border border-[#e0e0e0]'>
											<div className='w-full'>
												<p className='text-[16px] font-medium text-start text-[#4F5257]'>
													Data início
												</p>
												<input
													type='date'
													name='dataInicio'
													value={
														popupExtraFields.dataInicio
													}
													onChange={
														handlePopupExtraFieldChange
													}
													className={getModalDateFieldClass(
														popupExtraFields.dataInicio,
													)}
													style={{
														height: MODAL_FIELD_HEIGHT,
													}}
												/>
											</div>
											<div className='w-full'>
												<p className='text-[16px] font-medium text-start text-[#4F5257]'>
													Data fim
												</p>
												<input
													type='date'
													name='dataFim'
													value={
														popupExtraFields.dataFim
													}
													onChange={
														handlePopupExtraFieldChange
													}
													className={getModalDateFieldClass(
														popupExtraFields.dataFim,
													)}
													style={{
														height: MODAL_FIELD_HEIGHT,
													}}
												/>
											</div>
										</div>
									)}

									{(popupStatus === 'atestado' ||
										popupStatus === 'folga' ||
										popupStatus === 'licenca') && (
										<div className='w-full grid grid-cols-1 sm:grid-cols-3 gap-3 bg-[#fcfcfc] p-2 rounded-lg border border-[#e0e0e0]'>
											<div className='col-span-1 font-inter grid grid-cols-1 sm:grid-cols-2 gap-3'>
												<div className='w-full'>
													<p className='text-[16px] font-medium text-start text-[#4F5257]'>
														Data início
													</p>
													<input
														type='date'
														name='dataInicio'
														value={
															popupExtraFields.dataInicio
														}
														onChange={
															handlePopupExtraFieldChange
														}
														className={getModalDateFieldClass(
															popupExtraFields.dataInicio,
														)}
														style={{
															height: MODAL_FIELD_HEIGHT,
															...getLockedFieldStyle(
																lockedProfessionalFields.nome,
															),
														}}
													/>
												</div>
												<div className='w-full'>
													<p className='text-[16px] font-medium text-start text-[#4F5257]'>
														Data fim
													</p>
													<input
														type='date'
														name='dataFim'
														value={
															popupExtraFields.dataFim
														}
														onChange={
															handlePopupExtraFieldChange
														}
														className={getModalDateFieldClass(
															popupExtraFields.dataFim,
														)}
														style={{
															height: MODAL_FIELD_HEIGHT,
															...getLockedFieldStyle(
																lockedProfessionalFields.admissao,
															),
														}}
													/>
												</div>
											</div>
											<div className='col-span-1 sm:col-span-2 w-full'>
												<p className='text-[16px] font-medium text-start text-[#4F5257]'>
													Observação
												</p>
												<input
													type='text'
													name='observacao'
													placeholder='Observação'
													value={
														popupExtraFields.observacao
													}
													onChange={
														handlePopupExtraFieldChange
													}
													className={getModalFieldClass(
														popupExtraFields.observacao,
													)}
													style={{
														height: MODAL_FIELD_HEIGHT,
													}}
												/>
											</div>
										</div>
									)}

									{popupStatus === 'troca' && (
										<div className='w-full grid grid-cols-1 sm:grid-cols-3 gap-3 bg-[#fcfcfc] p-2 rounded-lg border border-[#e0e0e0]'>
											<div className='col-span-1 sm:col-span-2 w-full'>
												<p className='text-[16px] font-medium text-start text-[#4F5257]'>
													Trocar com
												</p>
												<select
													value={currentPro.trocaCom}
													onChange={(e) =>
														handleProInput(
															'trocaCom',
															e.target.value,
														)
													}
													className={getModalFieldClass(
														currentPro.trocaCom,
													)}
													style={{
														height: MODAL_FIELD_HEIGHT,
													}}>
													<option
														hidden
														disabled
														value=''
														{...SELECT_OPTION_PROPS}>
														Trocar com:
													</option>
													{profissionaisDaEscala.map(
														(profissional) => (
															<option
																key={
																	profissional.id
																}
																{...SELECT_OPTION_PROPS}
																value={
																	profissional.nome
																}>
																{
																	profissional.nome
																}
															</option>
														),
													)}
												</select>
											</div>
											<div className='col-span-1 font-inter grid grid-cols-1 sm:grid-cols-2 gap-3'>
												<div className='w-full'>
													<p className='text-[16px] font-medium text-start text-[#4F5257]'>
														Data
													</p>
													<select
														value={
															currentPro.trocaData
														}
														onChange={(e) =>
															handleProInput(
																'trocaData',
																e.target.value,
															)
														}
														className={getModalFieldClass(
															currentPro.trocaData,
														)}
														style={{
															height: MODAL_FIELD_HEIGHT,
															...getLockedFieldStyle(
																lockedProfessionalFields.vinculo,
															),
														}}>
														<option
															value=''
															{...SELECT_OPTION_PROPS}>
															00/00/0000
														</option>
														{datasTrocaDisponiveis.map(
															(item) => (
																<option
																	key={
																		item.value
																	}
																	{...SELECT_OPTION_PROPS}
																	value={
																		item.value
																	}>
																	{item.label}
																</option>
															),
														)}
													</select>
												</div>
												<div className='w-full'>
													<p className='text-[16px] font-medium text-start text-[#4F5257]'>
														Hora
													</p>
													<select
														value={
															currentPro.trocaHora
														}
														onChange={(e) => {
															const selected =
																horasTrocaDisponiveis.find(
																	(item) =>
																		item.value ===
																		e.target
																			.value,
																);
															setCurrentPro(
																(prev) => ({
																	...prev,
																	trocaHora:
																		e.target
																			.value,
																	trocaItemDestinoId:
																		String(
																			selected?.itemId ||
																				'',
																		),
																}),
															);
														}}
														className={getModalFieldClass(
															currentPro.trocaHora,
														)}
														style={{
															height: MODAL_FIELD_HEIGHT,
														}}>
														<option
															value=''
															{...SELECT_OPTION_PROPS}>
															00 às 00
														</option>
														{horasTrocaDisponiveis.map(
															(item) => (
																<option
																	key={
																		item.value
																	}
																	{...SELECT_OPTION_PROPS}
																	value={
																		item.value
																	}>
																	{item.label}
																</option>
															),
														)}
													</select>
												</div>
											</div>
										</div>
									)}

									<hr className='w-full  bg-[grey]'></hr>

									<div className='flex flex-col lg:flex-row gap-[12px]'>
										<div className='w-full  lg:min-w-[250px] lg:max-w-[380px] border border-[#E3E3E3] bg-white p-2 rounded-lg flex flex-col justify-start gap-[8px] font-inter text-[#757575] text-medium'>
											<div>
												<div className='border border-[#E3E3E3] bg-white items-start rounded-lg px-2 py-1'>
													<p className='text-start font-16px text-[#13335A]'>
														Regime
													</p>
													<div className='flex justify-start gap-4 mt-1 flex-wrap'>
														{regimeOptions.map(
															(option) => (
																<label
																	key={
																		option.value
																	}
																	className='flex gap-1'>
																	<input
																		type='radio'
																		name='popup-regime-enfermagem'
																		checked={
																			searchFilters.regime ===
																			String(
																				option.value,
																			).toLowerCase()
																		}
																		onChange={() =>
																			handleFilterInputChange(
																				'regime',
																				String(
																					option.value,
																				).toLowerCase(),
																			)
																		}
																	/>
																	{
																		option.label
																	}
																</label>
															),
														)}
													</div>
												</div>
											</div>

											<div className=' rounded-md  py-2'>
												<p className='text-center text-[16px]  capitalize text-[#6B7280]'>
													{getMonthlyLabel(
														searchFilters.mesVigente,
														searchFilters.anoVigente,
													)}
												</p>
												<div className='mt-3 grid grid-cols-7 mb-2 font-normal gap-y-2 text-center text-[12px] font-inter uppercase tracking-[0.15em] text-[#C0C4CC]'>
													{[
														'Dom',
														'Seg',
														'Ter',
														'Qua',
														'Qui',
														'Sex',
														'Sab',
													].map((dayLabel) => (
														<span key={dayLabel}>
															{dayLabel}
														</span>
													))}
												</div>
												<div className='mt-2 grid grid-cols-7 gap-y-2 text-center text-[16px] text-[#4F5257]'>
													{monthDays.map(
														(day, index) =>
															day ? (
																<button
																	type='button'
																	key={
																		day.iso
																	}
																	onClick={() =>
																		handleCalendarDaySelect(
																			day.iso,
																		)
																	}
																	className={`mx-auto flex h-8 w-8 items-center justify-center rounded-full transition-all ${(currentPro.selectedSlots || []).some((slot) => slot.data === day.iso) ? 'bg-[#3F7BA7] text-white shadow-sm' : 'hover:bg-[#EEF2F6]'}`}>
																	{day.day}
																</button>
															) : (
																<span
																	key={`empty-${index}`}
																/>
															),
													)}
												</div>
											</div>
										</div>

										{popupStatus === 'vacancia' ? (
											<div className='w-full rounded-lg border border-[#E3E3E3] bg-white p-3 font-inter'>
												<div className='w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-3 mb-3'>
													<div className='w-full'>
														<p className='text-[16px] from-normal  text-start text-[#13335A]'>
															Categoria
														</p>
														<select
															value={
																currentPro.categoria ||
																''
															}
															onChange={(e) =>
																handleProInput(
																	'categoria',
																	e.target
																		.value,
																)
															}
															className={getModalFieldClass(
																currentPro.categoria ||
																	'',
															)}
															style={{
																height: MODAL_FIELD_HEIGHT,
															}}>
															<option
																value=''
																{...SELECT_OPTION_PROPS}>
																Categoria
															</option>
															{categoriaOptions.map(
																(option) => (
																	<option
																		key={
																			option.id
																		}
																		{...SELECT_OPTION_PROPS}
																		value={
																			option.id
																		}>
																		{option.nome ||
																			option.label ||
																			option.descricao}
																	</option>
																),
															)}
														</select>
													</div>
													<div className='w-full'>
														<p className='text-[16px] from-normal  text-start text-[#13335A]'>
															Setor
														</p>
														<SearchableSetorSelect
															value={currentPro.setor || ''}
															options={setorOptions}
															onChange={(value) =>
																handleProInput('setor', value)
															}
															disabled={!setorOptions.length}
															className={getModalFieldClass(
																currentPro.setor || '',
																!setorOptions.length
																	? 'bg-[#E3E3E3] cursor-not-allowed text-[#AAB3BE]'
																	: '',
															)}
															style={{ height: MODAL_FIELD_HEIGHT }}
														/>
													</div>
													<div className='w-full'>
														<p className='text-[16px] from-normal  text-start text-[#13335A]'>
															Sala
														</p>
														<select
															value={
																currentPro.sala ||
																''
															}
															onChange={(e) =>
																handleProInput(
																	'sala',
																	e.target
																		.value,
																)
															}
															disabled={
																!salaOptions.length
															}
															className={getModalFieldClass(
																currentPro.sala ||
																	'',
																!salaOptions.length
																	? 'bg-[#E3E3E3] cursor-not-allowed text-[#AAB3BE]'
																	: '',
															)}
															style={{
																height: MODAL_FIELD_HEIGHT,
															}}>
															<option
																value=''
																{...SELECT_OPTION_PROPS}>
																Sala
															</option>
															{salaOptions.map(
																(option) => (
																	<option
																		key={
																			option.id
																		}
																		{...SELECT_OPTION_PROPS}
																		value={
																			option.id
																		}>
																		{
																			option.nome
																		}
																	</option>
																),
															)}
														</select>
													</div>
												</div>
												<p className='text-[16px] font-medium text-start text-[#13335A] mb-2'>
													Observação
												</p>
												<textarea
													name='observacao'
													placeholder='Observação'
													value={
														popupExtraFields.observacao
													}
													onChange={
														handlePopupExtraFieldChange
													}
													className={getModalFieldClass(
														popupExtraFields.observacao,
														'resize-none min-h-[280px] h-auto',
													)}
												/>
											</div>
										) : (
											<div className='w-full  grid grid-cols-1 sm:grid-cols-2 gap-3  font-inter'>
												<div className='w-full sm:col-span-2'>
													<p className='text-[16px] from-normal  text-start text-[#13335A]'>
														Nome
													</p>
													<input
														placeholder='Nome completo'
														value={currentPro.nome}
														disabled={
															lockedProfessionalFields.nome
														}
														onChange={(e) =>
															handleProInput(
																'nome',
																e.target.value,
															)
														}
														className={getModalFieldClass(
															currentPro.nome,
															getLockedFieldStateClass(
																lockedProfessionalFields.nome,
															),
														)}
														style={{
															height: MODAL_FIELD_HEIGHT,
														}}
													/>
												</div>
												<div className='w-full'>
													<p className='text-[16px] from-normal  text-start text-[#13335A]'>
														Admissão
													</p>
													<input
														type='date'
														value={
															currentPro.admissao ||
															''
														}
														disabled={
															lockedProfessionalFields.admissao
														}
														onChange={(e) =>
															handleProInput(
																'admissao',
																e.target.value,
															)
														}
														className={getModalFieldClass(
															currentPro.admissao ||
																'',
															getLockedFieldStateClass(
																lockedProfessionalFields.admissao,
															),
														)}
														style={{
															height: MODAL_FIELD_HEIGHT,
														}}
													/>
												</div>
												<div className='w-full'>
													<p className='text-[16px] from-normal  text-start text-[#13335A]'>
														Vínculo
													</p>
													<select
														value={
															currentPro.vinculo
														}
														disabled={
															lockedProfessionalFields.vinculo
														}
														onChange={(e) =>
															handleProInput(
																'vinculo',
																e.target.value,
															)
														}
														className={getModalFieldClass(
															currentPro.vinculo,
															getLockedFieldStateClass(
																lockedProfessionalFields.vinculo,
															),
														)}
														style={{
															height: MODAL_FIELD_HEIGHT,
														}}>
														<option
															value=''
															{...SELECT_OPTION_PROPS}>
															Vínculo
														</option>
														{vinculoOptions.map(
															(option) => (
																<option
																	key={
																		option.id
																	}
																	{...SELECT_OPTION_PROPS}
																	value={
																		option.id
																	}>
																	{option.nome ||
																		option.descricao ||
																		option.id}
																</option>
															),
														)}
													</select>
												</div>
												<div className='w-full sm:col-span-2 grid grid-cols-1 md:grid-cols-[0.82fr_1.18fr] gap-3'>
													<div className='w-full'>
														<p className='text-[16px] from-normal  text-start text-[#13335A]'>
															Cargo
														</p>
														<select
															value={
																currentPro.cargo
															}
															disabled={
																lockedProfessionalFields.cargo
															}
															onChange={(e) =>
																handleProInput(
																	'cargo',
																	e.target
																		.value,
																)
															}
															className={getModalFieldClass(
																currentPro.cargo,
																getLockedFieldStateClass(
																	lockedProfessionalFields.cargo,
																),
															)}
															style={{
																height: MODAL_FIELD_HEIGHT,
																...getLockedFieldStyle(
																	lockedProfessionalFields.cargo,
																),
															}}>
															<option
																value=''
																{...SELECT_OPTION_PROPS}>
																Cargo
															</option>
															{normalizedCargoOptions.map(
																(option) => (
																	<option
																		key={
																			option.value
																		}
																		{...SELECT_OPTION_PROPS}
																		value={
																			option.value
																		}>
																		{
																			option.label
																		}
																	</option>
																),
															)}
														</select>
													</div>
													<div className='w-full flex gap-3'>
														<div className='w-full'>
															<p className='text-[16px] font-normal text-start text-[#13335A]'>
																Número do
																conselho
															</p>

															<input
																placeholder='000000'
																value={
																	currentPro.numeroConselho
																}
																disabled={
																	lockedProfessionalFields.numeroConselho
																}
																onChange={(e) =>
																	handleProInput(
																		'numeroConselho',
																		e.target
																			.value,
																	)
																}
																className={getModalFieldClass(
																	currentPro.numeroConselho,
																	getLockedFieldStateClass(
																		lockedProfessionalFields.numeroConselho,
																	),
																)}
																style={{
																	height: MODAL_FIELD_HEIGHT,
																	...getLockedFieldStyle(
																		lockedProfessionalFields.numeroConselho,
																	),
																}}
															/>
														</div>
														<div className='w-[70px] shrink-0'>
															<p className='text-[16px] font-normal text-start text-[#13335A]'>
																UF
															</p>
															<select
																value={
																	currentPro.ufConselho
																}
																disabled={
																	lockedProfessionalFields.ufConselho
																}
																onChange={(e) =>
																	handleProInput(
																		'ufConselho',
																		e.target
																			.value,
																	)
																}
																className={getModalFieldClass(
																	currentPro.ufConselho,
																	getLockedFieldStateClass(
																		lockedProfessionalFields.ufConselho,
																	),
																)}
																style={{
																	height: MODAL_FIELD_HEIGHT,
																	...getLockedFieldStyle(
																		lockedProfessionalFields.ufConselho,
																	),
																}}>
																{UF_OPTIONS.map(
																	(uf) => (
																		<option
																			key={
																				uf
																			}
																			{...SELECT_OPTION_PROPS}
																			value={
																				uf
																			}>
																			{uf}
																		</option>
																	),
																)}
															</select>
														</div>
													</div>
												</div>
												<div className='w-full'>
													<p className='text-[16px] from-normal  text-start text-[#13335A]'>
														Especialidade
													</p>
													<select
														value={
															currentPro.especialidade
														}
														disabled={
															lockedProfessionalFields.especialidade
														}
														onChange={(e) =>
															handleProInput(
																'especialidade',
																e.target.value,
															)
														}
														className={getModalFieldClass(
															currentPro.especialidade,
															getLockedFieldStateClass(
																lockedProfessionalFields.especialidade,
															),
														)}
														style={{
															height: MODAL_FIELD_HEIGHT,
															...getLockedFieldStyle(
																lockedProfessionalFields.especialidade,
															),
														}}>
														<option
															value=''
															{...SELECT_OPTION_PROPS}>
															Especialidade
														</option>
														{normalizedEspecialidadeEnfermagemOptions.map(
															(option) => (
																<option
																	key={
																		option.value
																	}
																	{...SELECT_OPTION_PROPS}
																	value={
																		option.value
																	}>
																	{
																		option.label
																	}
																</option>
															),
														)}
													</select>
												</div>
												<div className='w-full'>
													<p className='text-[16px] from-normal  text-start text-[#13335A]'>
														Setor
													</p>
													<SearchableSetorSelect
														value={currentPro.setor || ''}
														options={setorOptions}
														onChange={(value) => handleProInput('setor', value)}
														className={getModalFieldClass(currentPro.setor || '')}
														style={{ height: MODAL_FIELD_HEIGHT }}
													/>
												</div>
												{/* <div className='w-full'>
											<p className='text-[16px] from-normal  text-start text-[#13335A]'>
												Sala
											</p>
											<select
												value={currentPro.sala}
												onChange={(e) =>
													handleProInput(
														'sala',
														e.target.value,
													)
												}
												className={getModalFieldClass(
													currentPro.sala,
												)}
												style={{
													height: MODAL_FIELD_HEIGHT,
												}}>
												<option
													value=''
													{...SELECT_OPTION_PROPS}>
													Sala
												</option>
												{salaOptions.map((option) => (
													<option
														key={option.id}
														{...SELECT_OPTION_PROPS}
														value={option.id}>
														{option.nome}
													</option>
												))}
											</select>
										</div> */}
												<div className='w-full'>
													<p className='text-[16px] flex from-normal text-start text-[#13335A]'>
														Carga horária semanal
													</p>
													<div className='relative border rounded-lg px-2.5 py-1.5 h-[36px] flex justify-start border-[#E3E3E3] items-center bg-white'>
														{shouldShowCargaHorariaTotal ? (
															<>
																<input
																	type='number'
																	readOnly
																	value={
																		cargaHorariaMensal
																	}
																	className={`outline-none rounded-lg text-[14px] font-medium text-start ${getFieldToneClass(cargaHorariaMensal)} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none bg-white w-[52px]`}
																/>
																<span className='pointer-events-none absolute left-[54px] top-1/2 -translate-y-1/2 text-[14px] text-[#8d8d8d]'>
																	Horas
																</span>
															</>
														) : (
															<span className='pointer-events-none text-[14px] text-[#CBD3DD] font-medium'>
																00 Horas
															</span>
														)}
													</div>
												</div>
												<div className='w-full'>
													<p className='text-[16px] from-normal  text-start text-[#13335A]'>
														Telefone
													</p>
													<input
														placeholder='(00) 00000-0000'
														value={
															currentPro.telefone
														}
														disabled={
															lockedProfessionalFields.telefone
														}
														onChange={(e) =>
															handleProInput(
																'telefone',
																formatPhoneNumber(
																	e.target
																		.value,
																),
															)
														}
														className={getModalFieldClass(
															currentPro.telefone,
															getLockedFieldStateClass(
																lockedProfessionalFields.telefone,
															),
														)}
														style={{
															height: MODAL_FIELD_HEIGHT,
															...getLockedFieldStyle(
																lockedProfessionalFields.telefone,
															),
														}}
													/>
												</div>
											</div>
										)}
									</div>

									<div
										className={`rounded-md bg-white transition-all duration-300 ease-out overflow-hidden ${hasSelectedSlots ? 'max-h-[220px] opacity-100 border border-[#E3E3E3] px-3 py-3 mt-0' : 'max-h-0 opacity-0 border border-transparent px-3 py-0 mt-0 pointer-events-none'}`}>
										<div className='flex flex-wrap items-end gap-3'>
											{(
												currentPro.selectedSlots || []
											).map((slot) => (
												<div
													key={slot.data}
													className='min-w-[92px] flex-1 max-w-[100px]'>
													<div className='mb-1 flex items-center gap-2'>
														<span className='text-[14px] font-medium text-[#6B7280]'>
															Dia{' '}
															{Number(
																String(
																	slot.data,
																).split('-')[2],
															)}
														</span>
													</div>
													<div className='flex gap-[1px] px-1 py-[4px] border text-[16px] rounded-md border-[#e7e7e7] bg-white'>
														<input
															type='number'
															className='outline-none w-[30px] text-center bg-white text-[#6B7280] text-[14px] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
															placeholder='00'
															min={0}
															max={23}
															inputMode='numeric'
															value={
																slot.horaInicio ||
																''
															}
															onChange={(e) =>
																handleSelectedSlotInputChange(
																	slot.data,
																	'horaInicio',
																	e.target
																		.value,
																)
															}
															onInput={(e) => {
																e.target.value =
																	sanitizeHourValue(
																		e.target
																			.value,
																	);
															}}
															onBlur={() =>
																handleSelectedSlotBlur(
																	slot.data,
																	'horaInicio',
																)
															}
														/>
														<span className='text-xs sm:text-[14px] flex items-center justify-center text-[#4F5257]'>
															às
														</span>
														<input
															type='number'
															className='outline-none w-[25px] text-center bg-white text-[#6B7280] text-[14px] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
															placeholder='00'
															min={0}
															max={23}
															inputMode='numeric'
															value={
																slot.horaFim ||
																''
															}
															onChange={(e) =>
																handleSelectedSlotInputChange(
																	slot.data,
																	'horaFim',
																	e.target
																		.value,
																)
															}
															onInput={(e) => {
																e.target.value =
																	sanitizeHourValue(
																		e.target
																			.value,
																	);
															}}
															onBlur={() =>
																handleSelectedSlotBlur(
																	slot.data,
																	'horaFim',
																)
															}
														/>
														<BsClock className='w-[16px] h-auto text-[#6B7280]' />
													</div>
												</div>
											))}
										</div>
									</div>

									{popupSubmitError && (
										<div className='w-full text-center text-[#dc2626] text-[14px] font-medium mt-2 whitespace-pre-line'>
											{popupSubmitError}
										</div>
									)}

									<div className='w-full flex justify-center mt-2'>
										<button
											onClick={handlePopupSubmit}
											disabled={isSubmittingPopup}
											className='bg-white text-[#727272] border border-[#cfcfcf] font-inter rounded-md w-full sm:w-auto px-6 sm:px-16 py-1.5 text-[16px] shadow-md hover:bg-[#b8b8b8] hover:text-white transition-all disabled:opacity-60 disabled:cursor-not-allowed'>
											{isSubmittingPopup
												? 'Salvando...'
												: isEditing
													? 'Salvar'
													: 'Enviar'}
										</button>
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

export default FormScale;
